import { liveReload } from '@shared/services/realtime.service';
import { sortCatalog } from '@app/shared/utils/sort.utils';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { catalogueTitle } from '../catalogue-titles';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
//Shared Services
import { CustomMessageService } from '../../shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { TableStructureModel } from '../../shared/models/custom-table.model';
import { UploadFileDialogService } from '@app/shared/custom-dialogs/upload-file-dialog/upload-file-dialog.service';
import { SimpleExcelUploadService } from '@app/shared/services/simple-excel-upload.service';
import { ExcelUploaderConfig } from '@app/shared/models/simple-excel-upload.model';
import { CustomDialogComponent } from '../../shared/components/custom-dialog/custom-dialog.component';
import { EquipmentSsaDialogComponent } from '../equipment-ssa-dialog/equipment-ssa-dialog.component';
import { EquipmentStorageDialogComponent } from '../equipment-storage-dialog/equipment-storage-dialog.component';
import { EquipmentConfigurationsDialogComponent } from '../equipment-configurations-dialog/equipment-configurations-dialog.component';
import { DialogData } from '../../shared/models/custom-dialog.model';
import { FieldType } from '../../shared/models/custom-dialog.model';
import { NetworkService } from '../../shared/services/network.service';
import { UploadDownloadService } from '@app/shared/services/upload-download.service';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';
//Angular
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MaterialModule } from '../../material.module';
import { Location } from '@angular/common';
//Auth/system entity services
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolesService } from '@app/shared/services/entities/roles.service';
import { ModulesService } from '@app/shared/services/modules.service';
import { PermissionsService } from '@app/shared/services/permissions.service';
import { Role } from '@app/shared/models/entities/roles.model';
import { ModuleModel } from '@app/shared/models/entities/module.model';
import { PermissionModel } from '@app/shared/models/entities/permission.model';
//Entity services
import { PlacesService } from '@app/shared/services/entities/places.service';
import { WarehousesService } from '@app/shared/services/entities/warehouse.service';
import { ProcedureService } from '@app/shared/services/entities/procedure.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { SupplierService } from '@app/shared/services/entities/supplier.service';
import { BrandsService } from '@app/shared/services/entities/brands.service';
import { ManufacturerService } from '@app/shared/services/entities/manufacturers.service';
import { EquipmentsService } from '@app/shared/services/entities/equipments.service';
import { InhousesService } from '@app/shared/services/entities/inhouses.service';
import { RfidDevicesService, RFID_DEVICE_TYPES, RFID_WAREHOUSE_OPTIONAL, rfidTypeFromText, RFID_PROVIDERS, rfidProviderFromText } from '@app/shared/services/entities/rfid-devices.service';
import { InstrumentalsService } from '@app/shared/services/entities/instrumentals.service';
import { AffilatesService } from '@app/shared/services/entities/affilates.service';
import { ClientsService } from '@app/shared/services/entities/clients.service';
import { SpecialtyService } from '@app/shared/services/entities/specialty.service';
import { HospitalServicesService } from '@app/shared/services/entities/hospitalServices.service';
import { KitService } from '@app/shared/services/entities/kit.service';
import { SparePartsService } from '@app/shared/services/entities/spareParts.service';
import { MeasurementInstrumentsService } from '@app/shared/services/entities/measurementInstruments.service';
import { MedicalGasTanksService } from '@app/shared/services/entities/medicalGasTanks.service';
import { MeasurementUnitsService } from '@app/shared/services/entities/measurementUnits.service';
import { PaymentMethodsService } from '@app/shared/services/entities/paymentMethods.service';
import { CoursesService } from '@app/shared/services/entities/courses.service';
import { GAS_CYLINDER_COLORS, gasColorHex, gasColorLabel } from '@app/shared/utils/gas-colors';
import { COUNTRY_OPTIONS } from '@app/shared/utils/countries';
import { TaxRegimesService } from '@app/shared/services/entities/taxRegimes.service';
import { AccessoriesService } from '@app/shared/services/entities/accessories.service';
import { PeripheralEquipmentService } from '@app/shared/services/entities/peripheralEquipment.service';
import { FixedAssetConfigurationsService } from '@app/shared/services/entities/fixedAssetConfigurations.service';
import { FixedAssetServiceConfigurationsService } from '@app/shared/services/entities/fixedAssetServiceConfigurations.service';
import { SuppliesService } from '@app/shared/services/entities/supplies.service';
import { InstitutionsService } from '@app/shared/services/entities/institutions.service';
import { DoctorsService } from '@app/shared/services/entities/doctors.service';
import { SubstitutesService } from '@app/shared/services/entities/substitutes.service';

//Para llamar servicios de entidades dinamicamente
interface BaseService {
  search(data:any): any;
  update(data:any): any;
  create(data:any): any;
}

/**
 * Convención de permisos en BD (sus `name`):
 *  - LEER, CREAR, EDITAR, BORRAR
 */
const PERMISSION_NAMES = {
  READ:   'LEER',
  CREATE: 'CREAR',
  UPDATE: 'EDITAR',
  DELETE: 'BORRAR',
} as const;

/**
 * Columnas que en BD guardan un ID de otra entidad. Su valor legible
 * (resuelto por changeIds() en `_<key>`) es lo que se muestra en tabla.
 * Todas las demás columnas se leen directo del registro.
 */
const RESOLVED_ID_COLUMNS = new Set<string>([
  // Tipo de tecnología RFID: clave → texto visible (changeIds, case 'rfidDevices').
  'rfidType',
  'specialty',
  'manufacturer',
  'supplier',
  'equipment',
  'brand',
  'place',
  'client',
  'affiliate',
  'instrumental',
  'product',
  'sparePart',
  'procedure',
  'contract',
  'role',
  'warehouse',
  // Código UDM del catálogo de Unidades de Medida (insumos).
  'UOM_code',
  // Lista de equipos ligados (instrumentos de medición, accesorios…).
  'equipments',
  // Booleano "Titular de Reg SSA": se resuelve a 'Sí'/'No' en changeIds
  // y se muestra como _ssa_reg_holder en la tabla.
  'ssa_reg_holder',
  // `services` no es un ID directo sino un arreglo de
  // { service, subDireccion }. Lo resolvemos a un string legible
  // en changeIds() (case 'places') y lo guardamos en _services.
  'services',
  // `contacts` es un arreglo de { name, phone, email }. Se resuelve a
  // un string legible en changeIds() (case 'suppliers').
  'contacts',
  // `roles` es un arreglo de IDs del catálogo de roles. Se resuelve a
  // los nombres legibles en changeIds() (case 'courses') y se guarda en _roles.
  'roles',
  // `specialty` en equipos es un arreglo de IDs (multiselect). Se resuelve a
  // nombres separados por coma en changeIds() (case 'equipments').
  // `measurement_instruments` es un arreglo de IDs del catálogo de
  // Instrumentos de Medición. Se resuelve en changeIds() a _measurement_instruments.
  'measurement_instruments',
  // Régimen fiscal del cliente: id → "Clave — Descripción" en changeIds (_tax_regime).
  'tax_regime',
  // Arrays de ids en Equipos: se resuelven a texto legible en changeIds.
  'accessories',
  'peripheral_equipment',
  'gas_tanks',
  // `equipments` (Equipos ligados) es un arreglo de ids del catálogo de
  // Equipos (accesorios / configuraciones). Se resuelve en changeIds.
  'equipments',
  // `institution` (Sitios tipo Hospital) es un id del catálogo de CLIENTES
  // (antes, del catálogo de
  // Instituciones. Se resuelve en changeIds (case 'places').
  'institution',
  // `spare_parts` es un arreglo de ids del catálogo de Refacciones
  // (configuración de servicio de activo fijo).
  'spare_parts',
  // Productos sustitutos: `productId` es el id del producto y `substitutes`
  // el arreglo de ids de sus sustitutos. Ambos salen del catálogo de
  // productos y se resuelven en changeIds() (case 'substitutes').
  'productId',
  'substitutes',
  // Par de sustitutos: `substituteId` también es un id de producto.
  'substituteId',
  // Servicios hospitalarios: `products` es un arreglo de ids de productos;
  // se resuelve a códigos (_products) y descripciones (_productDescriptions).
  'products',
  'productDescriptions',
  'productQuantities',
  'productNotes',
  // Médicos: hospitales (por alias) y variantes de nombre.
  'places',
  'nameVariants',
]);

/**
 * Columnas que en BD guardan la URL/path de un archivo subido al
 * UploadDownloadService (S3). En la tabla se renderizan con botones
 * para Subir / Reemplazar / Ver. Cada entrada define:
 *   - accept: filtro del <input type="file">
 *   - label:  texto descriptivo para mensajes y tooltips
 *   - icon:   icono de Material a mostrar
 *   - isImage: true ⇒ se muestra una miniatura cuando hay valor
 */
const FILE_UPLOAD_COLUMNS: Record<string, {
  accept: string;
  label:  string;
  icon:   string;
  isImage: boolean;
}> = {
  image:          { accept: 'image/*',                                                     label: 'imagen',              icon: 'image',       isImage: true  },
  user_manual:    { accept: '.pdf,application/pdf',                                        label: 'manual de usuario',   icon: 'menu_book',   isImage: false },
  service_manual: { accept: '.pdf,application/pdf',                                        label: 'manual de servicio',  icon: 'build',       isImage: false },
  datasheet:      { accept: '.pdf,application/pdf',                                        label: 'ficha técnica',       icon: 'description', isImage: false },
  catalog:        { accept: '.pdf,application/pdf',                                        label: 'catálogo',            icon: 'inventory_2', isImage: false },
  safety_sheet:   { accept: '.pdf,application/pdf',                                        label: 'hoja de seguridad',   icon: 'health_and_safety', isImage: false },
  agreement_file: { accept: '.pdf,application/pdf',                                        label: 'acuerdo / contrato',  icon: 'gavel',       isImage: false },
  // ── Insumos ──
  brochure:       { accept: '.pdf,application/pdf',                                        label: 'folleto',             icon: 'article',     isImage: false },
  sanitary_reg:   { accept: '.pdf,application/pdf',                                        label: 'registro sanitario',  icon: 'verified',    isImage: false },
  // ── Equipos: certificados de calidad del fabricante ──
  iso_13485:      { accept: '.pdf,application/pdf',                                        label: 'ISO 13485',           icon: 'workspace_premium', isImage: false },
  iso_9001:       { accept: '.pdf,application/pdf',                                        label: 'ISO 9001',            icon: 'military_tech',     isImage: false },
};


/**
 * Ajustes de etiqueta por catálogo. La llave es `<ruta>:<campo>`.
 * Sirve para que una columna genérica (p. ej. `image`) se llame distinto
 * según el catálogo: en Filiales es el LOGO de la empresa.
 */
const FILE_UPLOAD_LABEL_OVERRIDES: Record<string, { label?: string; icon?: string }> = {
  'affiliate:image': { label: 'logo', icon: 'domain' },
};


/**
 * Etiqueta de un equipo del catálogo: "CÓDIGO · MODELO · DESCRIPCIÓN".
 * Se usa en todos los selectores de "Equipos ligados" y al resolverlos en la
 * tabla, para que el usuario identifique el equipo sin ambigüedad (antes sólo
 * se mostraba código y descripción, y dos equipos podían verse idénticos).
 */
/** Etiqueta de un sitio: "ALIAS — Nombre" (el alias es como lo nombran
 *  los archivos de Médicos y Equipos). */
function formatPlaceLabel(p: any): string {
  if (!p) return '';
  return p.alias ? `${p.alias} — ${p.name ?? ''}` : (p.name ?? '');
}

/** Tipos de sitio. `Hospital` se conserva tal cual porque otras pantallas
 *  filtran por ese valor; CEDIS/Almacén quedan por los registros viejos. */
/** Opciones del Grupo de productos (catálogo de Productos). */
const PRODUCT_GROUP_OPTIONS = [
  { id: 'SES',   name: 'SES' },
  { id: 'EMID',  name: 'EMID' },
  { id: 'STI',   name: 'STI' },
  { id: 'OTROS', name: 'OTROS' },
];

/** Tipos de ubicación: siempre en MAYÚSCULAS (se guardan y se muestran así). */
const PLACE_TYPE_OPTIONS = [
  {id: 'HOSPITAL', name: 'HOSPITAL'},
  {id: 'MACROCENTRO', name: 'MACROCENTRO'},
  {id: 'ALMACEN LOCAL', name: 'ALMACÉN LOCAL'},
];

const OWNERSHIP_OPTIONS = [
  {id: 'PROPIEDAD', name: 'Propiedad'},
  {id: 'COMODATO', name: 'Comodato'},
  {id: 'ARRENDAMIENTO', name: 'Arrendamiento'},
  {id: 'DEMO', name: 'Demo'},
  {id: 'PRESTAMO', name: 'Préstamo'},
];

function formatEquipmentLabel(e: any): string {
  if (!e) return '';
  return [e.name ?? e.code, e.model, e.description]
    .map((v: any) => (v ?? '').toString().trim())
    .filter((v: string) => !!v)
    .join(' · ');
}


/**
 * Helper para mostrar un supplier con su intern_Id si lo tiene.
 * "PROV-001 — ACME Médica"  o solo "ACME Médica" si no tiene intern_Id.
 *
 * Lo usamos tanto en el label del dropdown (al crear/editar equipment)
 * como en la columna "Proveedor" de la tabla (vía changeIds), para que
 * la representación sea consistente.
 */
function formatSupplierLabel(s: any): string {
  if (!s) return '';
  const internId = s.intern_Id ? String(s.intern_Id).trim() : '';
  const name     = s.name ? String(s.name).trim() : '';
  if (internId && name) return `${internId} — ${name}`;
  return name || internId || '';
}


/**
 * Etiqueta de un producto (consumible): "CÓDIGO · MODELO · DESCRIPCIÓN".
 *
 * Los productos NO tienen `name`, así que sin esto los dropdowns saldrían
 * vacíos. Se usa en el catálogo de Productos Sustitutos, tanto en los
 * selectores como al resolver las columnas en la tabla.
 */
/** Opciones fijas de LÍNEA y COT en Servicios Hospitalarios (del Excel de
 * Medtronic). Si el negocio agrega valores, se agregan aquí; en la base se
 * guardan como texto, así que los registros viejos no se rompen. */
const HOSPITAL_SERVICE_LINES: string[] = [
  'VER', 'ACCESO', 'ENERGÍA', 'BARIATRÍA', 'HERNIA', 'TÓRAX', 'HEMI',
  'FLUORESCENCIA', 'BARIATRÍA VO ROBOT', 'VISUALIZACIÓN', 'OTORRINO',
  'NAVEGADOR', 'CABEZA Y CUELLO',
];
const HOSPITAL_SERVICE_COTS: string[] = [
  'SGS', 'AC', 'LA', 'VS', 'SP', 'SY', 'OS', 'EVIR', 'ENT',
];

function formatProductLabel(p: any): string {
  if (!p) return '';
  return [p.code, p.model, p.description]
    .map((v: any) => (v ?? '').toString().trim())
    .filter((v: string) => !!v)
    .join(' · ');
}

@Component({
    selector: 'app-catalogue-template',
    standalone: true,
    templateUrl: './catalogue-template.component.html',
    styleUrls: ['./catalogue-template.component.scss'],
    imports: [PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, MaterialModule]
})

export class CatalogueTemplateComponent implements OnInit {
  //Angular
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private location = inject(Location);
  //Shared
  private customMessageService = inject(CustomMessageService);
  //Services
  private networkService = inject(NetworkService);
  private simpleExcel = inject(SimpleExcelService);
  private simpleExcelUpload = inject(SimpleExcelUploadService);
  private uploadFileDialog = inject(UploadFileDialogService);
  private uploadDownloadService = inject(UploadDownloadService);
  private usersService = inject(UsersService);
  private rolesService = inject(RolesService);
  private modulesService = inject(ModulesService);
  /** Etiqueta del módulo de este catálogo (tabla `modules`). */
  private moduleLabel = '';
  private permissionsService = inject(PermissionsService);
  //Entities
  private procedureService = inject(ProcedureService);
  private productsService = inject(ProductsService);
  private warehousesService = inject(WarehousesService);
  private placesService = inject(PlacesService);
  private brandService = inject(BrandsService);
  private supplierService = inject(SupplierService);
  private manufacturerService = inject(ManufacturerService);
  private equipmentsService = inject(EquipmentsService);
  private rfidDevicesService = inject(RfidDevicesService);
  private inhousesService = inject(InhousesService);
  private instrumentalService = inject(InstrumentalsService);
  private affiliateService = inject(AffilatesService);
  private clientService = inject(ClientsService);
  private specialtyService = inject(SpecialtyService);
  private sparePartsService = inject(SparePartsService);
  private measurementInstrumentsService = inject(MeasurementInstrumentsService);
  private medicalGasTanksService = inject(MedicalGasTanksService);
  private measurementUnitsService = inject(MeasurementUnitsService);
  private paymentMethodsService = inject(PaymentMethodsService);
  private hospitalServicesService = inject(HospitalServicesService);
  private kitService = inject(KitService);
  private coursesService = inject(CoursesService);
  private taxRegimesService = inject(TaxRegimesService);
  private accessoriesService = inject(AccessoriesService);
  private peripheralEquipmentService = inject(PeripheralEquipmentService);
  private fixedAssetConfigurationsService = inject(FixedAssetConfigurationsService);
  private fixedAssetServiceConfigurationsService = inject(FixedAssetServiceConfigurationsService);
  private suppliesService = inject(SuppliesService);
  private institutionsService = inject(InstitutionsService);
  private doctorsService = inject(DoctorsService);
  private substitutesService = inject(SubstitutesService);

  isSlowConnection = false;

  // ─── Toggle de vista Tabla / Cartas ───────────────────────────────
  // Solo aplica a un subconjunto de catálogos (ver supportsCardView()).
  // La preferencia se persiste por ruta en localStorage.
  viewMode: 'table' | 'cards' = 'table';

  public permissions = {
    create: false,
    read:   false,
    update: false,
    delete: false,
  };

  accessState = signal<'loading' | 'granted' | 'denied' | 'no-module' | 'no-role'>('loading');
  blockedModuleLabel = signal<string>('este catálogo');

  public createBtn = false;

  public entityLists: {[key: string]: any} = {};
  public entityObjects: {[key: string]: any} = {};

  catalogue: string = '';
  cat:string = '';
  route: string = '';
  keys: any[] = [];
  keyNames: any[] = [];
  inputs: any[] =  [];
  public dataList: any[] = [];
  public dataSource = new MatTableDataSource();
  public dataService: any;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Set con las filas que están subiendo un archivo (para deshabilitar el botón).
  // Clave: `${recordId}::${fieldKey}` — así varios uploads pueden coexistir.
  public uploadingKeys = new Set<string>();

  // ─── Selección múltiple para acciones masivas ─────────────────────
  // Se compara por `id` para que la selección sobreviva a una recarga
  // (changeIds devuelve objetos nuevos). `multiple = true`.
  public selection = new SelectionModel<any>(
    true,
    [],
    true,
    (a, b) => a?.id != null && a.id === b?.id,
  );

  // Bandera para evitar abrir varios diálogos de bulk-edit en paralelo
  private bulkActionRunning = false;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(() => { const s: any = this.callAnyEntityService(this.dataService); return s?.resourceName ? [s.resourceName] : []; }, () => this.pageLoad.ready && this.pageLoad.refresh(this.getEntityData()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    this.route = this.router.url.replace(/\/+$/, '').split('/').filter(s => s.length > 0).pop() ?? '';

    // Restaura la preferencia de vista (tabla/cartas) guardada para esta ruta.
    this.loadViewModePreference();

    this.networkService.isConnectionSlow$.subscribe(slow => {
      this.isSlowConnection = slow;
    });

    // "Verificando acceso…" ya lo cubre la pantalla de carga del módulo.
    await this.loadCurrentUserPermissions();

    if (this.accessState() === 'granted') {
      await this.getEntityData();
    }
  }

  /* ************************************ Carga Inicial ***************************************************** */

  async loadCurrentUserPermissions(): Promise<void> {
    try {
      let currentUser = this.usersService.currentUser();
      if (!currentUser) {
        await this.usersService.loadCurrentUser();
        currentUser = this.usersService.currentUser();
      }

      if (!currentUser) { this.accessState.set('no-role'); return; }
      if (!currentUser.role) { this.accessState.set('no-role'); return; }

      const rolesResponse = await this.rolesService.search({
        searchType: 'default',
        condition: { id: currentUser.role },
      });
      const role: Role | undefined = rolesResponse.data?.[0];
      if (!role) { this.accessState.set('no-role'); return; }

      const modulesResponse = await this.modulesService.search({
        searchType: 'default',
        condition: { deleted: false },
      });
      const allModules: ModuleModel[] = modulesResponse.data ?? [];

      const currentRoute = this.normalizeRoute(this.router.url);
      const matchingModule = allModules.find(
        m => this.normalizeRoute(m.route ?? '') === currentRoute
      );

      // Módulo desactivado: se trata igual que si no existiera.
      if (!matchingModule || !matchingModule.id || matchingModule.active === false) {
        this.accessState.set('no-module');
        this.blockedModuleLabel.set(this.routeToHumanLabel(this.route));
        return;
      }

      this.blockedModuleLabel.set(matchingModule.label || this.routeToHumanLabel(this.route));
      // El título del catálogo es la etiqueta del módulo (la que se edita en
      // Módulos y se ve en la tarjeta y el menú lateral).
      this.moduleLabel = (matchingModule.label || matchingModule.name || '').trim();
      if (this.moduleLabel) this.catalogue = this.moduleLabel;

      const permsResponse = await this.permissionsService.search({
        searchType: 'default',
        condition: { deleted: false },
      });
      const allPerms: PermissionModel[] = permsResponse.data ?? [];
      const permIdToName = new Map<string, string>(
        allPerms.map(p => [p.id!, (p.name ?? '').toUpperCase()])
      );

      const grantedIds: string[] = role.accessPermissions?.[matchingModule.id] ?? [];
      const grantedNames = new Set<string>(
        grantedIds
          .map(id => permIdToName.get(id))
          .filter((n): n is string => !!n)
      );

      this.permissions = {
        read:   grantedNames.has(PERMISSION_NAMES.READ),
        create: grantedNames.has(PERMISSION_NAMES.CREATE),
        update: grantedNames.has(PERMISSION_NAMES.UPDATE),
        delete: grantedNames.has(PERMISSION_NAMES.DELETE),
      };

      this.accessState.set(this.permissions.read ? 'granted' : 'denied');
    } catch (err) {
      console.error('[CatalogueTemplate] Error verificando permisos:', err);
      this.accessState.set('denied');
    }
  }

  private normalizeRoute(route: string): string {
    return route
      .toLowerCase()
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');
  }

  private routeToHumanLabel(routeKey: string): string {
    const labels: Record<string, string> = {
      warehouses:    'Lockers',
      clients:       'Clientes',
      products:      'Insumos',
      equipments:    'Equipos',
      specialties:   'Especialidades',
      supplies:      'Insumos',
      institutions:  'Instituciones',
      fixedAssetConfigurations:        'Configuraciones de Activo Fijo',
      fixedAssetServiceConfigurations: 'Configuraciones de Servicio de Activo Fijo',
      manufacturers: 'Fabricantes',
      brands:        'Marcas',
      affiliates:    'Filiales',
      measurementInstruments: 'Instrumentos de Medición',
      medicalGasTanks: 'Tanques de Gas (Catálogo)',
      measurementUnits: 'Unidades de Medida',
      paymentMethods: 'Métodos de Pago',
      instrumentals: 'Instrumentales',
      procedures:    'Procedimientos',
      suppliers:     'Proveedores',
      spareParts:    'Refacciones',
      places:        'Ubicaciones',
      hospitalServices: 'Servicios Hospitalarios',
      kits:          'Kits',
      doctors:       'Médicos',
      substitutes:   'Insumos Sustitutos',
    };
    return labels[routeKey] ?? 'este catálogo';
  }

  goBackToMenu(): void { this.router.navigate(['/catalogues/menu']); }
  navigateTo(route: string): void { this.router.navigate([route]); }

  isResolvedIdColumn(key: string): boolean {
    return RESOLVED_ID_COLUMNS.has(key);
  }

  /* ************************************ Vista Tabla / Cartas ******************** */

  /**
   * Rutas que soportan la vista alternativa de tarjetas. Para el resto
   * de catálogos NO se muestra el toggle y se mantiene la tabla.
   */
  /** Lockers agrupados por ubicación, para el select dependiente (Tecnología RFID). */
  lockersByPlace(): { [placeId: string]: { label: string; value: any }[] } {
    const map: { [placeId: string]: { label: string; value: any }[] } = {};
    for (const w of (this.entityLists['warehousesList'] ?? []) as any[]) {
      if (!w?.place || w.active === false) continue;
      (map[w.place] ??= []).push({ label: [w.name, w.location].filter(Boolean).join(' · '), value: w.id });
    }
    return map;
  }

  /** Locker por (ubicación, nombre) — el nombre se repite entre hospitales. */
  private lockerIdFor(placeId: any, name: any): string | null {
    const n = this.upperText(name);
    if (!placeId || !n) return null;
    const w = ((this.entityLists['warehousesList'] ?? []) as any[]).find(x => x?.place === placeId && this.upperText(x?.name) === n);
    return w?.id ?? null;
  }

  /** Sitios tipo Hospital (para el alias de Lockers). */
  hospitalPlaces(): any[] {
    return (this.entityLists['placesList'] ?? []).filter((p: any) =>
      String(p?.type ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase() === 'HOSPITAL');
  }

  /** Grupo de productos del Excel → una de las opciones válidas (o null). */
  productGroupFromExcel(v: any): string | null {
    const t = String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
    if (!t) return null;
    if (t === 'OTRO') return 'OTROS';
    return PRODUCT_GROUP_OPTIONS.some(o => o.id === t) ? t : null;
  }

  warehouseTypeForPlace(placeId: any): string | null {
    const place = (this.entityLists['placesList'] ?? []).find((p: any) => p.id === placeId);
    const t = String(place?.type ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
    const map: Record<string, string> = { 'MACROCENTRO': 'CEDIS_MACRO', 'ALMACEN LOCAL': 'CEDIS_FSL', 'HOSPITAL': 'CONSIGNA', 'CEDIS': 'CEDIS', 'ALMACEN': 'ALMACEN' };
    return map[t] ?? null;
  }

  /** Todos los catálogos tienen vista de tabla y de tarjetas. */
  supportsCardView(): boolean {
    return true;
  }

  /** Tarjetas que se pintan (de a 60, con "Ver más", para catálogos grandes). */
  cardLimit = 60;
  showMoreCards(): void { this.cardLimit += 60; }

  /** Cambia la vista y persiste la preferencia por ruta en localStorage. */
  setViewMode(mode: 'table' | 'cards'): void {
    if (!mode) return;
    this.viewMode = mode;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`catalogueViewMode:${this.route}`, mode);
      } catch { /* almacenamiento no disponible: se ignora */ }
    }
  }

  /** Lee la preferencia guardada para la ruta actual (si existe). */
  private loadViewModePreference(): void {
    if (!this.supportsCardView()) { this.viewMode = 'table'; return; }
    if (typeof localStorage !== 'undefined') {
      try {
        const saved = localStorage.getItem(`catalogueViewMode:${this.route}`);
        this.viewMode = saved === 'cards' ? 'cards' : 'table';
      } catch {
        this.viewMode = 'table';
      }
    }
  }

  /**
   * Título mostrado en la cabecera de cada tarjeta. Usa el primer campo
   * identificador disponible en el registro.
   */
  cardTitle(element: any): string {
    const direct = element?.code ?? element?.instrument ?? element?.name ?? element?.part_number ?? element?.alias;
    if (direct) return String(direct);
    // Sin campo "nombre": la primera columna visible del catálogo.
    const first = this.cardFieldKeys()[0];
    return first ? String(this.cardValue(element, first) ?? '') : '';
  }

  /** ¿El catálogo tiene foto (columna `image`)? */
  get hasImageColumn(): boolean {
    return (this.keys ?? []).includes('image');
  }

  /**
   * Columnas de `keys` que se listan como pares "keyName: valor" en la
   * tarjeta. Excluye columnas de control (select/more/image/active) y
   * las columnas de archivo (manuales, datasheet, etc.).
   */
  /** Columnas de archivo (foto, manuales, etc.): en la tarjeta van como acciones. */
  cardFileKeys(): string[] {
    return (this.keys ?? []).filter((key: string) => this.isFileUploadColumn(key));
  }

  cardFieldKeys(): string[] {
    return (this.keys ?? []).filter((key: string) =>
      key !== 'select' &&
      key !== 'more' &&
      key !== 'image' &&
      key !== 'active' &&
      !this.isFileUploadColumn(key)
    );
  }

  /** Etiqueta (keyName) correspondiente a una columna, por índice en keys. */
  keyNameFor(key: string): string {
    const idx = this.keys.indexOf(key);
    return idx >= 0 ? (this.keyNames[idx] ?? key) : key;
  }

  /**
   * Fuente de datos de las tarjetas: respeta el filtro de búsqueda
   * (usa `filteredData`) y lo expone como `any[]` para el template.
   */
  get cardData(): any[] {
    return ((this.dataSource?.filteredData ?? []) as any[]).slice(0, this.cardLimit);
  }

  get cardTotal(): number {
    return (this.dataSource?.filteredData ?? []).length;
  }

  /** Valor a mostrar en la tarjeta para una columna (resuelto o crudo). */
  cardValue(element: any, key: string): any {
    if (this.isResolvedIdColumn(key)) {
      return element?.['_' + key] ?? '';
    }
    return element?.[key] ?? '';
  }

  /* ************************************ Uploads de archivos ********************** */

  /** ¿Esta columna corresponde a un campo que se sube vía UploadDownloadService? */
  isFileUploadColumn(key: string): boolean {
    return key in FILE_UPLOAD_COLUMNS;
  }

  /**
   * Metadata de la columna de upload (accept, icon, label, isImage).
   *
   * `image` es una columna genérica que varios catálogos reutilizan, pero en
   * cada uno significa algo distinto. En Filiales es el LOGO de la empresa,
   * así que los tooltips y mensajes deben decir "logo" y no "imagen".
   */
  fileUploadMeta(key: string) {
    const meta = FILE_UPLOAD_COLUMNS[key];
    if (!meta) return meta;

    const override = FILE_UPLOAD_LABEL_OVERRIDES[`${this.route}:${key}`];
    return override ? { ...meta, ...override } : meta;
  }

  /**
   * URL lista para pintar en un `<img>`.
   *
   * ⚠️ En la tabla se hacía `<img [src]="element[key]">`, pero lo que se
   * persiste es la KEY de S3 (`dev/products/<id>/foto.png`), no una
   * URL: la miniatura salía siempre rota y por eso el logo "no se veía".
   * `resolveImageColumns()` firma esas keys y deja el resultado en
   * `_<campo>Url`; aquí sólo se lee, con respaldo por si el dato heredado ya
   * era una URL completa.
   */
  imageUrlOf(element: any, key: string): string {
    const resolved = element?.[`_${key}Url`];
    if (resolved) return resolved;
    const raw = element?.[key];
    return typeof raw === 'string' && /^(https?:|data:)/i.test(raw) ? raw : '';
  }

  /**
   * Firma las columnas de tipo imagen de los registros visibles para poder
   * mostrar la miniatura. Se hace una sola vez por registro (se memoriza en
   * `_<campo>Url`) y en paralelo, para no firmar en cada ciclo de render.
   */
  private async resolveImageColumns(records: any[]): Promise<void> {
    const imageKeys = (this.keys ?? []).filter(
      (k: string) => this.isFileUploadColumn(k) && FILE_UPLOAD_COLUMNS[k]?.isImage,
    );
    if (!imageKeys.length || !records?.length) return;

    const pending: Promise<void>[] = [];

    for (const element of records) {
      for (const key of imageKeys) {
        const raw = element?.[key];
        if (!raw || element[`_${key}Url`]) continue;

        // Dato heredado que ya guardaba una URL completa: se usa tal cual.
        if (/^(https?:|data:)/i.test(String(raw))) {
          element[`_${key}Url`] = raw;
          continue;
        }

        pending.push(
          this.uploadDownloadService.presign(String(raw))
            .then(file => { if (file?.url) element[`_${key}Url`] = file.url; })
            .catch(err => console.warn('[catalogue] No se pudo firmar la imagen:', key, err)),
        );
      }
    }

    if (!pending.length) return;
    await Promise.all(pending);
    // Refrescamos la tabla para que se pinten las miniaturas ya firmadas.
    this.dataSource.data = [...this.dataList];
  }

  /**
   * Hex a pintar para un valor de color guardado. El valor normalmente ya
   * es un hex (lo guarda el select), pero si viniera un nombre (datos
   * viejos o carga por Excel) lo resolvemos contra la lista fija.
   * Devuelve '' cuando no hay color, para que el template pinte transparente.
   */
  colorHex(value: any): string { return gasColorHex(value); }

  /** Nombre legible de un color guardado (hex → nombre; fallback al valor). */
  colorLabel(value: any): string { return gasColorLabel(value); }

  /** Clave estable para identificar un upload en curso (row + campo). */
  private uploadKey(element: any, key: string): string {
    return `${element?.id ?? '_'}::${key}`;
  }

  /** ¿Estamos subiendo este archivo ahora mismo? Útil para el spinner. */
  isUploadingFile(element: any, key: string): boolean {
    return this.uploadingKeys.has(this.uploadKey(element, key));
  }

  /**
   * Click en "Subir / Reemplazar / Ver". Abre el diálogo de gestión de
   * archivos (cargar / previsualizar / descargar / reemplazar / quitar).
   * Al cerrarse con "Guardar", persiste la key S3 devuelta en el campo
   * correspondiente del registro.
   *
   * Ruta lógica en S3: `${this.route}/${element.id}`, con folio = key.
   * El folio nos permite tener varios archivos por entidad (manual,
   * datasheet, etc.) sin pisarse entre ellos. Es el mismo path/folio
   * que usaba el upload manual previo.
   */
  triggerFileUpload(element: any, key: string): void {
    if (!this.permissions['update']) return;
    if (!element?.id) return; // necesitamos el id para construir el path

    const meta = FILE_UPLOAD_COLUMNS[key];
    if (!meta) return;

    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:       element[key] ?? '',
        label:       this.capitalize(meta.label),
        accept:      meta.accept ?? '*/*',
        uploadPath:  `${this.route}/${element.id}`,
        uploadFolio: key,
      },
      width: '680px', maxHeight: '90vh',
    });

    ref.afterClosed().subscribe((result: string | null) => {
      if (result === null || result === undefined) return; // cancelado
      void this.persistUploadedFile(element, key, result);
    });
  }

  /**
   * Persiste en el backend la key S3 devuelta por el FileManager
   * (cadena vacía si se quitó el archivo) y refresca el dato en memoria.
   */
  private async persistUploadedFile(element: any, key: string, value: string): Promise<void> {
    const meta = FILE_UPLOAD_COLUMNS[key];
    if (!meta) return;

    const stateKey = this.uploadKey(element, key);
    this.uploadingKeys.add(stateKey);

    try {
      const service = this.callAnyEntityService(this.dataService);
      if (!service) {
        throw new Error(`No se encontró servicio para "${this.dataService}".`);
      }

      const apiResponse = await service.update({
        id: element.id,
        [key]: value,
        operationType: 'single',
      });

      if (apiResponse?.state !== 'success') {
        throw new Error(this.extractErrorMessage(apiResponse?.message ?? 'No se pudo guardar el archivo.'));
      }

      // Actualizar el dato en memoria sin re-fetch completo
      element[key] = value;
      this.dataSource.data = [...this.dataList];

      await this.customMessageService.open({
        title: 'Éxito', type: 'success',
        message: value
          ? `${this.capitalize(meta.label)} cargado correctamente.`
          : `${this.capitalize(meta.label)} eliminado correctamente.`,
        duration: 1800,
      });
    } catch (err) {
      console.error('[persistUploadedFile] Error:', err);
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: `No se pudo guardar el archivo: ${this.extractErrorMessage(err)}`,
        dismissBtn: true,
      });
    } finally {
      this.uploadingKeys.delete(stateKey);
    }
  }

  /** Abre el gestor de archivos para ver/descargar el documento de la fila. */
  viewUploadedFile(element: any, key: string): void {
    this.triggerFileUpload(element, key);
  }

  private capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /*** @description Función en la que se declara la info para cada catálogo según la ruta
   *
   * Sobre `labelFn` en los inputs tipo 'select':
   * - Por default, los dropdowns muestran `option.name` como label.
   * - Si necesitas mostrar otra cosa (ej. "INTERN_ID — Nombre" para
   *   suppliers), agrega `labelFn: (opt) => string` al input.
   * - El value SIEMPRE es `option.id`. labelFn solo afecta el texto
   *   visible en el dropdown.
   */
  private setRoutes(): Record<string, any>{
    return {
            'suppliers':{dataService: 'supplier',
                        catalogue: 'Proveedores',
                        cat: 'proveedor',
                        // Mismo criterio de unicidad que el backend
                        // (unique_key=['intern_Id','name','legal_name','tax_id']).
                        // Sin esto la detección de duplicados del front tomaba
                        // TODOS los campos de texto (dirección, CP, departamento,
                        // ejecutivo de cuenta…) y bloqueaba altas legítimas de
                        // proveedores que comparten domicilio o departamento.
                        unique: ['intern_Id', 'name', 'legal_name', 'tax_id'],
                        keys: ['intern_Id', 'name', 'legal_name', 'tax_id', 'address', 'zip_code', 'contacts', 'type', 'responsible_department', 'risk_classification', 'evaluation_frequency', 'contract_time', 'last_evaluation_date', 'next_evaluation_date', 'account_executive', 'payment_conditions', 'ship_to', 'attention_hours', 'payment_currency', 'createdOn', 'active', 'agreement_file', 'more'],
                        keyNames: ['Id Interno', 'Razón Social', 'Nombre Comercial', 'RFC', 'Dirección', 'Código Postal', 'Contactos', 'Tipo',  'Departamento', 'Riesgo', 'Frecuencia de evaluación', 'Tiempo de contrato', 'Última evaluación', 'Próxima evaluación', 'Ejecutivo de cuenta', 'Condiciones de pago', 'Enviar a (ventas)', 'Horarios de atención', 'Divisa de pago', 'Alta', 'Activo', 'Acuerdo / Contrato', 'Más'],
                        inputs: [{'name': 'intern_Id','displayName': 'Id Interno','type': 'text','required': true},
                                {'name': 'name','displayName': 'Razón Social','type': 'text','required': true},
                                {'name': 'legal_name','displayName': 'Nombre Comercial','type': 'text','required': true},
                                {'name': 'tax_id','displayName': 'RFC','type': 'text','required': true},
                                {'name': 'address','displayName': 'Dirección','type': 'text','required': true},
                                {'name': 'zip_code','displayName': 'Código Postal','type': 'text','required': true},
                                // Repeater de contactos: N filas con { name, phone, email }.
                                {'name': 'contacts','displayName': 'Contactos','type': 'contacts','required': false,
                                  'addLabel': 'Agregar contacto',
                                  'columns': [
                                    { key: 'name',  label: 'Nombre',   type: 'text' },
                                    { key: 'phone', label: 'Teléfono', type: 'tel' },
                                    { key: 'email', label: 'Correo',   type: 'email' },
                                  ]},
                                {'name': 'type', 'displayName': 'Tipo', 'type': 'select', 'options': [{id: 'Servicio técnico', name: 'Servicio técnico'}, {id: 'Calibración ', name: 'Calibración '}, {id: 'Capacitaciones', name: 'Capacitaciones'}],'required': true},
                                {'name': 'responsible_department','displayName': 'Departamento','type': 'text','required': false},
                                {'name': 'risk_classification', 'displayName': 'Riesgo', 'type': 'select', 'options': [{id: 'Bajo', name: 'Bajo'}, {id: 'Medio', name: 'Medio'}, {id: 'Alto', name: 'Alto'}],'required': false},
                                {'name': 'evaluation_frequency','displayName': 'Frecuencia de evaluación','type': 'number','required': false},
                                {'name': 'contract_time','displayName': 'Tiempo de contrato','type': 'number','required': false},
                                {'name': 'last_evaluation_date','displayName': 'Última evaluación','type': 'number','required': false},
                                // ── Datos comerciales (se precargan en la Orden de Compra) ──
                                {'name': 'account_executive','displayName': 'Ejecutivo de cuenta','type': 'text','required': false},
                                {'name': 'payment_conditions','displayName': 'Condiciones de pago','type': 'text','required': false},
                                {'name': 'ship_to','displayName': 'Enviar a (contacto de ventas)','type': 'text','required': false},
                                {'name': 'attention_hours','displayName': 'Horarios de atención','type': 'text','required': false},
                                {'name': 'payment_currency', 'displayName': 'Divisa de pago', 'type': 'select', 'options': [{id: 'MXN', name: 'MXN'}, {id: 'USD', name: 'USD'}, {id: 'EUR', name: 'EUR'}, {id: 'GBP', name: 'GBP'}],'required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
'affiliate':{
    dataService: 'affiliate',
    catalogue: 'Filiales',
    cat: 'filial',
    keys:     ['name',               'address',   'tax_id', 'image', 'active', 'more'],
    keyNames: ['Nombre Comercial',   'Dirección', 'RFC',    'Logo',  'Activo', 'Más'],
    //                                              ↑ agregué 'RFC'
    inputs: [
        {'name': 'name','displayName': 'Nombre Comercial','type': 'text','required': true},
        {'name': 'address','displayName': 'Dirección','type': 'text','required': true},
        {'name': 'tax_id','displayName': 'RFC','type': 'text','required': true},
        {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}
    ]
},
            'manufacturers':{dataService: 'manufacturer',
                        catalogue: 'Fabricantes',
                        cat: 'fabricante',
                        keys: ['name', 'active','more'],
                        keyNames: ['Nombre','Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'measurementInstruments':{dataService: 'measurementInstruments',
                        // Cargamos el catálogo de marcas al entrar para
                        // poblar el select de "Marca" y resolverlo en la
                        // tabla (id → nombre) vía changeIds().
                        entities: ['brand', 'equipment'],
                        catalogue: 'Instrumentos de Medición',
                        cat: 'instrumento de medición',
                        // Un instrumento de medición es único por la COMBINACIÓN
                        // instrumento + marca + modelo (mismo criterio que el
                        // unique_key del backend). Sin esto, la detección de
                        // duplicados caía al modo "cualquier campo de texto" y
                        // bloqueaba casos legítimos:
                        //   - dos instrumentos distintos de la misma marca+modelo
                        //   - dos simuladores de la misma marca con distinto modelo
                        //   - varios registros con modelo "N/A"
                        unique: ['instrument', 'brand', 'model'],
                        keys: ['instrument', 'brand', 'model', 'equipments', 'active', 'more'],
                        keyNames: ['Instrumento', 'Marca', 'Modelo', 'Equipos ligados', 'Activo', 'Más'],
                        inputs: [{'name': 'instrument','displayName': 'Instrumento','type': 'text','required': true},
                                 // La marca NO se escribe libre: viene del
                                 // catálogo de Marcas para evitar duplicados
                                 // tipo "FLUKE" vs "Fluke" vs "fluke ".
                                 {'name': 'brand','displayName': 'Marca','type': 'select','options': this.entityLists['brandList'] ?? [],'required': true},
                                 {'name': 'model','displayName': 'Modelo','type': 'text','required': false},
                                 // Un instrumento de medición puede servir a VARIOS
                                 // equipos: pasó de un select único a lista.
                                 {'name': 'equipments','displayName': 'Equipos ligados','type': 'multiautocomplete','options': this.entityLists['equipmentList'] ?? [],'labelFn': formatEquipmentLabel,'required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'medicalGasTanks':{dataService: 'medicalGasTanks',
                        // Cargamos proveedores para el select y su resolución
                        // id → nombre en la tabla (vía changeIds).
                        entities: ['supplier', 'equipment'],
                        catalogue: 'Tanques de Gas (Catálogo)',
                        cat: 'tipo de tanque de gas',
                        keys: ['code', 'gas', 'color', 'cas', 'capacity', 'presentation', 'working_pressure', 'reg_ssa', 'safety_sheet', 'supplier', 'equipment', 'active', 'more'],
                        keyNames: ['Código', 'Gas', 'Color', 'CAS', 'Capacidad', 'Presentación', 'Presión de trabajo', 'Registro SSA', 'Hoja de Seguridad', 'Proveedor', 'Equipo', 'Activo', 'Más'],
                        inputs: [{'name': 'code','displayName': 'Código','type': 'text','required': true},
                                 // El gas se elige de una lista (ids en MAYÚSCULAS
                                 // para que coincidan con el valor guardado).
                                 {'name': 'gas','displayName': 'Gas','type': 'select','required': true,
                                  'options': [
                                    {id: 'OXÍGENO (O2)',          name: 'Oxígeno (O2)'},
                                    {id: 'ÓXIDO NITROSO (N2O)',   name: 'Óxido nitroso (N2O)'},
                                    {id: 'DIÓXIDO DE CARBONO (CO2)', name: 'Dióxido de carbono (CO2)'},
                                    {id: 'AIRE MEDICINAL',        name: 'Aire medicinal'},
                                    {id: 'NITRÓGENO (N2)',        name: 'Nitrógeno (N2)'},
                                    {id: 'ARGÓN (AR)',            name: 'Argón (Ar)'},
                                    {id: 'VACÍO',                 name: 'Vacío'},
                                  ]},
                                 // Color de identificación del cilindro: lista fija (guarda el hex).
                                 {'name': 'color','displayName': 'Color','type': 'select','options': GAS_CYLINDER_COLORS.map(c => ({ id: c.hex, name: c.name })),'required': false},
                                 // Número CAS (Chemical Abstracts Service) del gas.
                                 {'name': 'cas','displayName': 'CAS','type': 'text','required': false},
                                 {'name': 'capacity','displayName': 'Capacidad (ej. 6 m³, 10 L)','type': 'text','required': false},
                                 {'name': 'presentation','displayName': 'Presentación (cilindro, termo…)','type': 'text','required': false},
                                 {'name': 'working_pressure','displayName': 'Presión de trabajo','type': 'text','required': false},
                                 // Registro sanitario SSA (número/referencia).
                                 {'name': 'reg_ssa','displayName': 'Registro SSA','type': 'text','required': false},
                                 {'name': 'description','displayName': 'Descripción','type': 'text','required': false},
                                 // Nota: 'safety_sheet' (Hoja de Seguridad, PDF) se sube desde la
                                 // columna de la tabla, igual que los manuales; no va en inputs.
                                 {'name': 'supplier','displayName': 'Proveedor','type': 'select','options': this.entityLists['supplierList'] ?? [], 'labelFn': formatSupplierLabel, 'required': false},
                                 {'name': 'equipment','displayName': 'Equipo','type': 'select','options': this.entityLists['equipmentList'] ?? [],'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),'required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'brands':{dataService: 'brand',
                        catalogue: 'Marcas',
                        cat: 'marca',
                        keys: ['name', 'active','more'],
                        keyNames: ['Nombre','Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'paymentMethods':{dataService: 'paymentMethods',
                        catalogue: 'Métodos de Pago',
                        cat: 'método de pago',
                        keys: ['name', 'satId', 'active','more'],
                        keyNames: ['Nombre','Id SAT','Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'satId','displayName': 'Id SAT','type': 'text','required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'measurementUnits':{dataService: 'measurementUnits',
                        catalogue: 'Unidades de Medida',
                        cat: 'unidad de medida',
                        keys: ['name', 'abbreviation', 'satId', 'active','more'],
                        keyNames: ['Nombre','Abreviación','Id SAT','Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'abbreviation','displayName': 'Abreviación','type': 'text','required': false},
                                 {'name': 'satId','displayName': 'Id SAT','type': 'text','required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'clients':{dataService: 'client',
                        entities: ['taxRegimes'],
                        catalogue: 'Clientes',
                        cat: 'cliente',
                        keys: ['name', 'legal_name', 'tax_id', 'tax_regime', 'address', 'zip_code', 'contact_name', 'contact_phone', 'contact_email', 'type', 'active','more'],
                        keyNames: ['Nombre','Razón Social', 'RFC', 'Régimen Fiscal', 'Dirección', 'Código Postal', 'Contacto', 'Teléfono', 'Correo', 'Tipo', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'legal_name','displayName': 'Razón Social','type': 'text','required': true},
                                 {'name': 'tax_id','displayName': 'RFC','type': 'text','required': true},
                                 {'name': 'tax_regime','displayName': 'Régimen Fiscal','type': 'autocomplete','options': (this.entityLists['taxRegimesList'] ?? []).map((r: any) => ({ value: r.id, label: `${r.code} — ${r.description}` })),'required': true},
                                 {'name': 'address','displayName': 'Dirección','type': 'text','required': true},
                                 {'name': 'zip_code','displayName': 'Código Postal','type': 'text','required': true},
                                 {'name': 'contact_name','displayName': 'Contacto','type': 'text','required': false},
                                 {'name': 'contact_phone','displayName': 'Teléfono','type': 'number','required': false},
                                 {'name': 'contact_email','displayName': 'Correo','type': 'email','required': false},
                                 {'name': 'type', 'displayName': 'Tipo', 'type': 'select', 'options': [{id: 'Servicio técnico', name: 'Servicio técnico'}, {id: 'Servicio intergral', name: 'Servicio intergral'}, {id: 'Venta Directa ', name: 'Venta Directa '}, {id: 'Renta', name: 'Renta'}],'required': true},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'instrumentals':{dataService: 'instrumental',
                        entities: ['specialty', 'manufacturer', 'places'],
                        catalogue: 'Instrumental',
                        cat: 'instrumental',
                        // Mismas columnas que el archivo de Medtronic:
                        // ESPECIALIDAD | NOMBRE | DESCRIPCIÓN | FABRICANTE | ALIAS.
                        // El mismo nombre puede estar en varios sitios: la llave es (nombre, sitio).
                        unique: ['name', 'place'],
                        keys: ['specialty', 'name', 'description', 'manufacturer', 'place', 'image', 'active', 'more'],
                        keyNames: ['Especialidad', 'Nombre', 'Descripción', 'Fabricante', 'Alias', 'Foto', 'Activo', 'Más'],
                        inputs: [{'name': 'specialty', 'displayName': 'Especialidad', 'type': 'select', 'options': this.entityLists['specialtyList'] ?? [], 'searchable': true, 'required': false},
                                 {'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                 {'name': 'description','displayName': 'Descripción','type': 'text','required': false},
                                 {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [], 'searchable': true, 'required': false},
                                 {'name': 'place','displayName': 'Alias (ubicación)','type': 'select','options': this.entityLists['placesList'] ?? [],'labelFn': formatPlaceLabel,'searchable': true,'required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'spareParts': {dataService: 'spareParts',
                        entities: ['specialty', 'manufacturer', 'supplier', 'equipment', 'brand'],
                        catalogue: 'Refacciones',
                        cat: 'refacción',
                        keys: ['part_number', 'description', 'brand', 'supplier', 'manufacturer', 'equipment', 'classification', 'specialty', 'service_life', 'min_stock', 'max_stock', 'priority_level', 'image', 'active','more'],
                        keyNames: ['Número de Parte', 'Descripción', 'Marca', 'Proveedor', 'Fabricante', 'Equipo', 'Clasificación', 'Especialidad', 'Vida de Servicio', 'Inventario Mínimo', 'Inventario Máximo', 'Prioridad', 'Foto', 'Activo', 'Más'],
                        inputs: [{'name': 'part_number','displayName': 'Número de Parte','type': 'text','required': true},
                                 {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                 {'name': 'brand', 'displayName': 'Marca', 'type': 'select', 'options': this.entityLists['brandList'] ?? [],'required': true},
                                 {'name': 'supplier', 'displayName': 'Proveedor', 'type': 'select', 'options': this.entityLists['supplierList'] ?? [], 'labelFn': formatSupplierLabel, 'required': true},
                                 {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [],'required': true},
                                 {
                                      'name': 'equipment',
                                      'displayName': 'Equipo',
                                      'type': 'select',
                                      'options': this.entityLists['equipmentList'] ?? [],
                                      'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),
                                      'required': true
                                  },
                                 {'name': 'classification', 'displayName': 'Clasificación', 'type': 'select', 'options': [{id: 'Hidráulico', name: 'Hidráulico'}, {id: 'Electrónico', name: 'Electrónico'}, {id: 'Mecánico', name: 'Mecánico'}],'required': false},
                                 {'name': 'specialty', 'displayName': 'Especialidad', 'type': 'select', 'options': this.entityLists['specialtyList'] ?? [],'required': true},
                                 {'name': 'service_life','displayName': 'Vida de Servicio','type': 'text','required': false},
                                 {'name': 'min_stock','displayName': 'Inventario Mínimo','type': 'number','required': false},
                                 {'name': 'max_stock','displayName': 'Inventario Máximo','type': 'number','required': false},
                                 {'name': 'priority_level', 'displayName': 'Prioridad', 'type': 'select', 'options': [{id: 'Bajo', name: 'Bajo'}, {id: 'Medio', name: 'Medio'}, {id: 'Alto', name: 'Alto'}],'required': false},
                                 // La Foto se sube/visualiza desde la tabla (columna 'image'),
                                 // igual que en el catálogo de Equipos.
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]
                      },
            'specialties': {dataService:'specialty',
                      catalogue: 'Especialidades',
                      cat: 'especialidad',
                      keys: ['name','active','more'],
                      keyNames: ['Nombre','Activo','Más'],
                      inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── MÉDICOS ─────────────────────────────────────────────────
            // Catálogo de captura mínima: nombre y especialidad. La
            // especialidad sale del catálogo de Especialidades (se guarda el
            // id y la tabla muestra el nombre, resuelto en changeIds).
            'doctors': {dataService:'doctors',
                      entities: ['places'],
                      catalogue: 'Médicos',
                      cat: 'médico',
                      // Archivo de Medtronic: una fila por NOMBRE + ALIAS del
                      // hospital. Aquí es un médico con la lista de hospitales
                      // donde opera. `nameVariants` son los nombres mal escritos
                      // (hoja ML_BASE_ERRORES) que corresponden a este médico.
                      unique: ['name'],
                      keys: ['name','places','active','more'],
                      keyNames: ['Nombre','Alias (hospitales)','Activo','Más'],
                      inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                              {'name': 'places','displayName': 'Hospitales (alias)','type': 'multiselect','options': this.entityLists['placesList'] ?? [],'labelFn': formatPlaceLabel,'required': true},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── PRODUCTOS SUSTITUTOS ────────────────────────────────────
            // Un renglón por producto con la lista de los productos que lo
            // pueden sustituir. TODAS las opciones (producto y sustitutos)
            // salen del catálogo de Consumibles vía el servicio de productos.
            'substitutes': {dataService:'substitutes',
                      entities: ['products'],
                      catalogue: 'Insumos Sustitutos',
                      cat: 'insumo sustituto',
                      // Un renglón por equivalencia (producto → sustituto),
                      // igual que el Excel de captura. La unicidad del PAR la
                      // valida también el backend (unique_key=['productId','substituteId']).
                      unique: ['productId', 'substituteId'],
                      keys: ['productId','substituteId','active','more'],
                      keyNames: ['Código de insumo','Código de insumo sustituto','Activo','Más'],
                      inputs: [{'name': 'productId','displayName': 'Insumo','type': 'select','options': this.entityLists['productsList'] ?? [],'labelFn': formatProductLabel,'searchable': true,'required': true},
                              {'name': 'substituteId','displayName': 'Insumo sustituto','type': 'select','options': this.entityLists['productsList'] ?? [],'labelFn': formatProductLabel,'searchable': true,'required': true},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'hospitalServices': {dataService:'hospitalServices',
                      entities: ['specialty', 'products'],
                      catalogue: 'Servicios Hospitalarios',
                      cat: 'servicio hospitalario',
                      // Archivo de Medtronic: una fila por PRODUCTO del servicio.
                      // Aquí es un servicio con sus renglones de productos
                      // ({code, description, maxQuantity, notes}). Un mismo
                      // CODIGO EPIONE trae varios CIR y nombres, así que la
                      // llave es (Código EPIONE, CIR, Nombre).
                      unique: ['epioneCode', 'cir', 'name'],
                      keys: ['specialty','line','cot','cir','mdtCode','name','products','productDescriptions','productQuantities','active','more'],
                      keyNames: ['Especialidad','Línea','COT','CIR','Código MDT','Nombre del servicio','Código de insumos','Descripción de insumos','Cantidad máx','Activo','Más'],
                      inputs: [{'name': 'specialty', 'displayName': 'Especialidad', 'type': 'select', 'options': this.entityLists['specialtyList'] ?? [],'required': true},
                              {'name': 'line', 'displayName': 'Línea', 'type': 'select', 'options': HOSPITAL_SERVICE_LINES.map(v => ({id: v, name: v})),'required': false},
                              {'name': 'cot', 'displayName': 'COT', 'type': 'select', 'options': HOSPITAL_SERVICE_COTS.map(v => ({id: v, name: v})),'required': false},
                              // CIR es un código libre y creciente (100078, NUEVO, PEN…).
                              {'name': 'cir','displayName': 'CIR','type': 'text','required': false},
                              {'name': 'mdtCode','displayName': 'Código MDT','type': 'text','required': false},
                              // Código EPIONE: solo llega por la carga de Excel; no se
                              // muestra ni se captura a mano (se conserva al editar).
                              {'name': 'name','displayName': 'Nombre del servicio','type': 'text','required': true},
                              // Un renglón por producto, como en el archivo. Cantidad
                              // vacía = comparte la del renglón anterior (alternativas).
                              {'name': 'products','displayName': 'Insumos del servicio','type': 'contacts','required': false,
                                'addLabel': 'Agregar insumo',
                                'columns': [
                                  { key: 'code',        label: 'Código',       type: 'text' },
                                  { key: 'description', label: 'Descripción',  type: 'text' },
                                  { key: 'maxQuantity', label: 'Cantidad máx', type: 'number' },
                                ]},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── KITS ────────────────────────────────────────────────────
            // Kits/charolas: se ofrecen como "Charola" en la salida de
            // material. `epioneCode` es un código propio legible (el `id` es
            // un UUID interno, no sirve para capturar). Los productos del kit
            // (arreglo {code, description, maxQuantity}) no se editan aquí.
            'kits': {dataService: 'kits',
                      catalogue: 'Kits',
                      cat: 'kit',
                      unique: ['name'],
                      keys: ['line','cot','cir','mdtCode','epioneCode','name','quantity','active','more'],
                      keyNames: ['Línea','COT','CIR','Código MDT','Código EPIONE','Nombre del kit','Cantidad','Activo','Más'],
                      inputs: [{'name': 'line', 'displayName': 'Línea', 'type': 'select', 'options': HOSPITAL_SERVICE_LINES.map(v => ({id: v, name: v})),'required': false},
                              {'name': 'cot', 'displayName': 'COT', 'type': 'select', 'options': HOSPITAL_SERVICE_COTS.map(v => ({id: v, name: v})),'required': false},
                              {'name': 'cir','displayName': 'CIR','type': 'text','required': false},
                              {'name': 'mdtCode','displayName': 'Código MDT','type': 'text','required': false},
                              {'name': 'epioneCode','displayName': 'Código EPIONE','type': 'text','required': false},
                              {'name': 'name','displayName': 'Nombre del kit','type': 'text','required': true},
                              {'name': 'quantity','displayName': 'Cantidad','type': 'number','required': false},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
                        // ── INSTITUCIONES ───────────────────────────────────────────
            // Catálogo simple (IMSS, ISSSTE, privadas...). Se referencia
            // desde Sitios cuando el tipo es Hospital.
            'institutions': {dataService:'institutions',
                      catalogue: 'Instituciones',
                      cat: 'institución',
                      keys: ['name','active','more'],
                      keyNames: ['Nombre','Activo','Más'],
                      inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'places': {dataService:'places',
                      catalogue: 'Ubicaciones',
                      cat: 'ubicación',
                      // Columnas del archivo de Medtronic: NOMBRE | ALIAS |
                      // UBICACIÓN | TIPO. El ALIAS es la llave con la que los
                      // archivos de Médicos y Equipos ligan un sitio.
                      unique: ['name'],
                      keys: ['name', 'alias', 'location','type', 'soldTo', 'active','more'],
                      keyNames: ['Nombre', 'Alias', 'Ubicación', 'Tipo', 'SoldTo SAP', 'Activo','Más'],
                      inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                              {'name': 'alias','displayName': 'Alias','type': 'text','required': true},
                              {'name': 'location','displayName': 'Ubicación','type': 'text','required': true},
                              {'name': 'type','displayName': 'Tipo','type': 'select','options': PLACE_TYPE_OPTIONS,'required': true},
                              // SoldTo de la cuenta en SAP: liga el Inventario SAP Consigna con el hospital (reporte Solicitado vs entregado).
                              {'name': 'soldTo','displayName': 'SoldTo SAP','type': 'text','required': false},
                              {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'products': {dataService:'products',
                        entities: ['manufacturer'],
                        catalogue: 'Insumos',
                        cat: 'insumo',
                        // Columnas del Excel de Medtronic. Los campos del
                        // modelo anterior (modelo, proveedor, equipo, stock
                        // mín/máx, regulación…) siguen en la base pero ya no
                        // se capturan ni se muestran.
                        keys: ['code','description','manufacturer','productGroup','alg','UOM','price','image','active','more'],
                        keyNames: ['Código','Descripción','Fabricante','Grupo de insumos','ALG','Unidad de Medida','Precio','Foto','Activo','Más'],
                        unique: ['code'],
                        inputs: [{'name': 'code','displayName': 'Código','type': 'text','required': true},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [], 'required': true},
                                {'name': 'productGroup','displayName': 'Grupo de insumos','type': 'select','options': PRODUCT_GROUP_OPTIONS,'required': false},
                                {'name': 'alg','displayName': 'ALG','type': 'text','required': false},
                                {'name': 'UOM', 'displayName': 'Unidad de Medida', 'type': 'select', 'options': [{id: 'Pieza', name: 'Pieza'}, {id: 'Caja', name: 'Caja'}],'required': true},
                                {'name': 'price','displayName': 'Precio','type': 'number','required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── EQUIPMENTS ──────────────────────────────────────────────
            // Mismas columnas que el archivo de Medtronic: NOMBRE DE EQUIPO |
            // ALIAS | RESPONSABLE DEL EQUIPO. El sitio se elige/carga por su
            // alias; la llave es (nombre, sitio) porque TORRE 1 existe en
            // varios hospitales.
            'equipments': {dataService: 'equipments',
                        entities: ['places'],
                        catalogue: 'Equipos',
                        cat: 'equipo',
                        unique: ['name', 'place'],
                        keys: ['name', 'place', 'responsible', 'image', 'active', 'more'],
                        keyNames: ['Nombre de equipo', 'Alias', 'Responsable del equipo', 'Foto', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre de equipo','type': 'text','required': true},
                                {'name': 'place','displayName': 'Alias (ubicación)','type': 'select','options': this.entityLists['placesList'] ?? [],'labelFn': formatPlaceLabel,'searchable': true,'required': true},
                                {'name': 'responsible','displayName': 'Responsable del equipo','type': 'text','required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── IN-HOUSE ────────────────────────────────────────────────
            // Almacén dentro del hospital que no es locker (bodega, cuarto de
            // material…). NOMBRE | ALIAS (HOSPITAL) | UBICACIÓN (texto libre).
            'inhouses': {dataService: 'inhouses',
                        entities: ['places'],
                        catalogue: 'In-house',
                        cat: 'in-house',
                        unique: ['name', 'place'],
                        keys: ['name', 'place', 'location', 'active', 'more'],
                        keyNames: ['Nombre', 'Alias (hospital)', 'Ubicación (dentro del hospital)', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                {'name': 'place','displayName': 'Alias (hospital)','type': 'select','options': this.hospitalPlaces(),'labelFn': formatPlaceLabel,'searchable': true,'required': true},
                                {'name': 'location','displayName': 'Ubicación (dentro del hospital)','type': 'text','required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── TECNOLOGÍA RFID ─────────────────────────────────────────
            // Cada dispositivo RFID (widget, bandeja, antenas, anaquel), en
            // qué ubicación (hospital) está y a qué locker da servicio. El
            // locker se elige entre los de la ubicación; para los widgets es
            // opcional porque se mueven entre lockers.
            'rfidDevices': {dataService: 'rfidDevices',
                        entities: ['places', 'warehouses'],
                        catalogue: 'Tecnología RFID',
                        cat: 'dispositivo RFID',
                        unique: ['name', 'place'],
                        keys: ['name', 'rfidType', 'provider', 'place', 'warehouse', 'serialNumber', 'responsible', 'image', 'active', 'more'],
                        keyNames: ['Nombre', 'Tipo de tecnología', 'Proveedor', 'Ubicación (alias)', 'Locker', 'No. de serie / ID', 'Responsable', 'Foto', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                {'name': 'type','displayName': 'Tipo de tecnología','type': 'select','options': [...RFID_DEVICE_TYPES],'required': true},
                                {'name': 'provider','displayName': 'Proveedor','type': 'select','options': [...RFID_PROVIDERS],'required': false},
                                {'name': 'place','displayName': 'Ubicación (hospital)','type': 'select','options': this.hospitalPlaces(),'labelFn': formatPlaceLabel,'searchable': true,'required': true},
                                {'name': 'warehouse','displayName': 'Locker (obligatorio salvo widgets)','type': 'select','options': this.entityLists['warehousesList'] ?? [],
                                 'dependsOn': 'place','optionsMap': this.lockersByPlace(),'searchable': true,'required': false},
                                {'name': 'serialNumber','displayName': 'No. de serie / ID','type': 'text','required': false},
                                {'name': 'responsible','displayName': 'Responsable','type': 'text','required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── WAREHOUSES ──────────────────────────────────────────────
            // Columnas del archivo de Medtronic: ALIAS | UBICACIÓN | NOMBRE.
            // ALIAS liga al sitio (catálogo de Sitios); UBICACIÓN es texto
            // libre (dónde está dentro del hospital/sitio). El tipo
            // (CEDIS_MACRO / CEDIS_FSL / CONSIGNA…) lo calcula la API a
            // partir del tipo del sitio; no se captura.
            'warehouses': {dataService:'warehouses',
                           entities: ['places'],
                           catalogue:'Lockers',
                           cat:'locker',
                           unique: ['name', 'place'],
                           keys: ['place', 'location', 'name', 'storageType', 'active', 'more'],
                           keyNames: ['Alias', 'Ubicación', 'Nombre', 'Tipo', 'Activo', 'Más'],
                           // Lockers: solo sitios tipo Hospital.
                           inputs: [{'name': 'place','displayName': 'Alias (hospital)','type': 'select','options': this.hospitalPlaces(),'labelFn': formatPlaceLabel,'searchable': true,'required': true},
                                    {'name': 'location','displayName': 'Ubicación (dentro del hospital)','type': 'text','required': false},
                                    {'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                    // Locker (gabinete) o Inhouse (almacén que atiende el inhouse).
                                    {'name': 'storageType','displayName': 'Tipo','type': 'select','options': [{id: 'LOCKER', name: 'Locker'}, {id: 'INHOUSE', name: 'Inhouse'}],'required': true},
                                    {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'procedures': {dataService: 'procedure',
                          // Solo se necesita Especialidad. 'contract' no tiene
                          // servicio en callAnyEntityService y tumbaba la carga
                          // del catálogo ("reading 'state'"); 'products' no se usaba.
                          entities: ['specialty'],
                          catalogue: 'Procedimientos',
                          cat: 'procedimiento',
                          keys: ['name', 'specialty','active', 'more'],
                          keyNames: ['Cirugía', 'Especialidad','Activo', 'Más'],
                          inputs: [{'name': 'name','displayName': 'Cirugía','type': 'text','required': true},
                                    {'name': 'specialty', 'displayName': 'Especialidad', 'type': 'select', 'options': this.entityLists['specialtyList'] ?? [], 'required': true},
                                    {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true},
                                  ]},
            // ── COURSES (catálogo de capacitación) ──────────────────────
            // `roles` es un multiselect contra el catálogo de Roles. En la
            // tabla se muestra como nombres legibles vía changeIds (_roles).
            'courses': {dataService: 'courses',
                       entities: ['roles', 'equipment'],
                       catalogue: 'Cursos',
                       cat: 'curso',
                       // Sólo el nombre del curso es único. Fase y área pueden
                       // repetirse entre cursos distintos, por eso acotamos la
                       // detección de duplicados a 'name' (de lo contrario, al
                       // ser fase/área inputs de texto, se marcarían como
                       // duplicados aunque deban poder repetirse).
                       unique: ['name'],
                       keys: ['name', 'description', 'phase', 'area', 'roles', 'equipment', 'active', 'more'],
                       keyNames: ['Nombre', 'Descripción', 'Fase', 'Área', 'Roles', 'Equipos', 'Activo', 'Más'],
                       inputs: [{'name': 'name','displayName': 'Nombre del curso','type': 'text','required': true},
                                 {'name': 'description','displayName': 'Descripción','type': 'text','required': false},
                                 {'name': 'phase','displayName': 'Fase','type': 'text','required': false},
                                 {'name': 'area','displayName': 'Área','type': 'text','required': false},
                                 {'name': 'roles','displayName': 'Roles','type': 'multiselect','options': this.entityLists['rolesList'] ?? [],'required': false},
                                 // Capacitación técnica: equipos médicos relacionados (multiselect).
                                 {'name': 'equipment','displayName': 'Equipos médicos','type': 'multiselect','options': this.entityLists['equipmentList'] ?? [],'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),'required': false},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            'taxRegimes':{dataService: 'taxRegimes',
                        catalogue: 'Régimen Fiscal',
                        cat: 'régimen fiscal',
                        keys: ['code', 'description', 'active', 'more'],
                        keyNames: ['Clave', 'Descripción', 'Activo', 'Más'],
                        inputs: [{'name': 'code','displayName': 'Clave','type': 'text','required': true},
                                 {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                 {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── ACCESSORIES ─────────────────────────────────────────────
            // Espejo del bloque 'equipments' + campo `equipment` (id del
            // equipo al que pertenece el accesorio).
            'accessories': {dataService: 'accessories',
                        entities: ['equipment', 'supplier', 'manufacturer', 'specialty', 'brand'],
                        catalogue: 'Accesorios',
                        cat: 'accesorio',
                        // Los manuales, ficha técnica y catálogo se retiraron:
                        // esa documentación vive en el EQUIPO, no en el accesorio.
                        keys: ['code', 'description', 'model', 'invoice_description', 'brand', 'supplier', 'manufacturer', 'specialty', 'equipments', 'image', 'active','more'],
                        keyNames: ['Código', 'Descripción', 'Modelo', 'Descripción Factura', 'Marca', 'Proveedor', 'Fabricante', 'Especialidad', 'Equipos ligados', 'Imagen',  'Activo', 'Más'],
                        inputs: [{'name': 'code','displayName': 'Código','type': 'text','required': true},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                {'name': 'model','displayName': 'Modelo','type': 'text','required': true},
                                {'name': 'invoice_description','displayName': 'Descripción Factura','type': 'text','required': false},
                                {'name': 'brand', 'displayName': 'Marca', 'type': 'select', 'options': this.entityLists['brandList'] ?? [],'required': true},
                                {'name': 'supplier', 'displayName': 'Proveedor', 'type': 'select', 'options': this.entityLists['supplierList'] ?? [], 'labelFn': formatSupplierLabel, 'required': true},
                                {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [], 'required': true},
                                {'name': 'specialty', 'displayName': 'Especialidades', 'type': 'multiselect', 'options': this.entityLists['specialtyList'] ?? [],'required': true},
                                // Equipos ligados: LISTA de equipos del catálogo a los que
                                // este accesorio puede pertenecer.
                                {'name': 'equipments','displayName': 'Equipos ligados','type': 'multiautocomplete','options': this.entityLists['equipmentList'] ?? [],'labelFn': formatEquipmentLabel,'required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── PERIPHERAL EQUIPMENT ────────────────────────────────────
            // Idéntico a accessories pero con su propio dataService/labels.
            'peripheralEquipment': {dataService: 'peripheralEquipment',
                        entities: ['equipment', 'supplier', 'manufacturer', 'brand'],
                        catalogue: 'Equipo Periférico',
                        cat: 'equipo periférico',
                        keys: ['code', 'model', 'invoice_description', 'description', 'brand', 'supplier', 'manufacturer', 'equipment', 'pm_frequency', 'user_manual', 'service_manual', 'datasheet', 'catalog', 'image', 'active','more'],
                        keyNames: ['Código', 'Modelo', 'Descripción Factura', 'Descripción', 'Marca', 'Proveedor', 'Fabricante', 'Equipo ligado', 'Frecuencia de Mantenimiento', 'Manual de Usuario', 'Manual de Servicio', 'Ficha Técnica', 'Catálogo', 'Imagen',  'Activo', 'Más'],
                        inputs: [{'name': 'code','displayName': 'Código','type': 'text','required': true},
                                {'name': 'model','displayName': 'Modelo','type': 'text','required': true},
                                {'name': 'invoice_description','displayName': 'Descripción Factura','type': 'text','required': false},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                {'name': 'brand', 'displayName': 'Marca', 'type': 'select', 'options': this.entityLists['brandList'] ?? [],'required': true},
                                {'name': 'supplier', 'displayName': 'Proveedor', 'type': 'select', 'options': this.entityLists['supplierList'] ?? [], 'labelFn': formatSupplierLabel, 'required': true},
                                {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [], 'required': true},
                                {'name': 'equipment','displayName': 'Equipo ligado','type': 'select','options': this.entityLists['equipmentList'] ?? [],'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),'required': false},
                                {'name': 'pm_frequency','displayName': 'Frecuencia de Mantenimiento','type': 'select', 'options': [{id: 'TRIMESTRAL', name: 'Trimestral'}, {id: 'SEMESTRAL', name: 'Semestral'}, {id: 'ANUAL', name: 'Anual'}, {id: 'LIBRE DE MANTENIMIENTO', name: 'Libre de mantenimiento'}],'required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── CONFIGURACIÓN DE ACTIVO FIJO ────────────────────────────
            // Configuraciones nombradas de un equipo: qué accesorios,
            // periféricos y tanques de gas componen cada configuración.
            'fixedAssetConfigurations': {dataService: 'fixedAssetConfigurations',
                        entities: ['equipment', 'accessories', 'peripheralEquipment', 'medicalGasTanks'],
                        catalogue: 'Configuración de Activo Fijo',
                        cat: 'configuración de activo fijo',
                        keys: ['name', 'equipment', 'description', 'accessories', 'peripheral_equipment', 'gas_tanks', 'active', 'more'],
                        keyNames: ['Nombre', 'Equipo', 'Descripción', 'Accesorios', 'Periféricos', 'Tanques de Gas', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                {'name': 'equipment','displayName': 'Equipo','type': 'select','options': this.entityLists['equipmentList'] ?? [],'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),'required': true},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': false},
                                {'name': 'accessories','displayName': 'Accesorios','type': 'multiautocomplete','options': this.entityLists['accessoriesList'] ?? [],'labelFn': (a: any) => a?.code ? `${a.code}${a?.description ? ' · ' + a.description : ''}` : (a?.description ?? ''),'required': false},
                                {'name': 'peripheral_equipment','displayName': 'Periféricos','type': 'multiautocomplete','options': this.entityLists['peripheralEquipmentList'] ?? [],'labelFn': (a: any) => a?.code ? `${a.code}${a?.description ? ' · ' + a.description : ''}` : (a?.description ?? ''),'required': false},
                                {'name': 'gas_tanks','displayName': 'Tanques de Gas','type': 'multiautocomplete','options': this.entityLists['medicalGasTanksList'] ?? [],'labelFn': (g: any) => g?.code ? `${g.code}${g?.gas ? ' · ' + g.gas : ''}` : (g?.gas ?? ''),'required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── CONFIGURACIÓN DE SERVICIO DE ACTIVO FIJO ────────────────
            // Plantillas de servicio por equipo: tipo de servicio,
            // instrumental de medición requerido y refacciones.
            'fixedAssetServiceConfigurations': {dataService: 'fixedAssetServiceConfigurations',
                        entities: ['equipment', 'measurementInstruments', 'spareParts'],
                        catalogue: 'Configuración de Servicio de Activo Fijo',
                        cat: 'configuración de servicio',
                        keys: ['name', 'equipment', 'service_type', 'description', 'measurement_instruments', 'spare_parts', 'active', 'more'],
                        keyNames: ['Nombre', 'Equipo', 'Tipo de Servicio', 'Descripción', 'Instrumental de Medición', 'Refacciones', 'Activo', 'Más'],
                        inputs: [{'name': 'name','displayName': 'Nombre','type': 'text','required': true},
                                {'name': 'equipment','displayName': 'Equipo','type': 'select','options': this.entityLists['equipmentList'] ?? [],'labelFn': (e: any) => e.code && e.description ? `${e.code} — ${e.description}` : (e.code ?? e.description ?? ''),'required': true},
                                {'name': 'service_type', 'displayName': 'Tipo de Servicio', 'type': 'select', 'options': [{id: 'Mantenimiento Preventivo', name: 'Mantenimiento Preventivo'}, {id: 'Mantenimiento Correctivo', name: 'Mantenimiento Correctivo'}, {id: 'Calibración', name: 'Calibración'}, {id: 'Instalación', name: 'Instalación'}, {id: 'Capacitación', name: 'Capacitación'}, {id: 'Otro', name: 'Otro'}],'required': true},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': false},
                                {'name': 'measurement_instruments','displayName': 'Instrumental de medición','type': 'multiautocomplete','options': this.entityLists['measurementInstrumentsList'] ?? [],'labelFn': (i: any) => i?.instrument ? `${i.instrument}${i?.model ? ' · ' + i.model : ''}` : (i?.model ?? ''),'required': false},
                                {'name': 'spare_parts','displayName': 'Refacciones','type': 'multiautocomplete','options': this.entityLists['sparePartsList'] ?? [],'labelFn': (s: any) => s?.part_number ? `${s.part_number}${s?.description ? ' · ' + s.description : ''}` : (s?.description ?? ''),'required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
            // ── INSUMOS ─────────────────────────────────────────────────
            // Insumo = lo que usa el paciente (Consumible = lo que usa el equipo).
            'supplies': {dataService: 'supplies',
                        // `measurementUnits` alimenta el desplegable de Código UDM.
                        entities: ['supplier', 'manufacturer', 'brand', 'measurementUnits'],
                        catalogue: 'Insumos',
                        cat: 'insumo',
                        unique: ['code'],
                        keys: ['code', 'description', 'model', 'clv', 'brand', 'supplier', 'manufacturer', 'origin_country', 'UOM', 'UOM_code', 'sanitary_reg_number', 'sanitary_reg', 'brochure', 'min_stock', 'max_stock', 'active', 'more'],
                        keyNames: ['Código', 'Descripción', 'Modelo', 'CLV', 'Marca', 'Proveedor', 'Fabricante', 'País de Origen', 'Unidad de Medida', 'Código UDM', 'No. Registro Sanitario', 'Registro Sanitario', 'Folleto', 'Inventario Mínimo', 'Inventario Máximo', 'Activo', 'Más'],
                        inputs: [{'name': 'code','displayName': 'Código','type': 'text','required': true},
                                {'name': 'description','displayName': 'Descripción','type': 'text','required': true},
                                {'name': 'model','displayName': 'Modelo','type': 'text','required': false},
                                // CLV = clave del insumo en el cuadro básico del sector salud.
                                {'name': 'clv','displayName': 'CLV (cuadro básico)','type': 'text','required': false},
                                {'name': 'brand', 'displayName': 'Marca', 'type': 'select', 'options': this.entityLists['brandList'] ?? [],'required': false},
                                {'name': 'supplier', 'displayName': 'Proveedor', 'type': 'select', 'options': this.entityLists['supplierList'] ?? [], 'labelFn': formatSupplierLabel, 'required': false},
                                {'name': 'manufacturer', 'displayName': 'Fabricante', 'type': 'select', 'options': this.entityLists['manufacturerList'] ?? [], 'required': false},
                                // País de origen: lista ISO fija (ver shared/utils/countries.ts).
                                {'name': 'origin_country','displayName': 'País de Origen','type': 'select','options': COUNTRY_OPTIONS,'searchable': true,'required': false},
                                {'name': 'UOM', 'displayName': 'Unidad de Medida', 'type': 'select', 'options': [{id: 'Caja', name: 'Caja'}, {id: 'Pieza', name: 'Pieza'}, {id: 'Paquete', name: 'Paquete'}, {id: 'Kit', name: 'Kit'}],'required': false},
                                // Código UDM: ya no es texto libre, se elige del
                                // catálogo de Unidades de Medida.
                                {'name': 'UOM_code','displayName': 'Código UDM','type': 'select','options': this.entityLists['measurementUnitsList'] ?? [],'labelFn': (u: any) => u?.code ? `${u.code} — ${u.name}` : (u?.name ?? ''),'searchable': true,'required': false},
                                {'name': 'sanitary_reg_number','displayName': 'No. de Registro Sanitario','type': 'text','required': false},
                                {'name': 'min_stock','displayName': 'Inventario Mínimo','type': 'number','required': false},
                                {'name': 'max_stock','displayName': 'Inventario Máximo','type': 'number','required': false},
                                {'name': 'active', 'displayName': 'Activo', 'type': 'select', 'options': [{id: true, name: 'Activo'}, {id: false, name: 'Inactivo'}],'required': true}]},
          }
  }

  /*** @description Función en la que se declara la información del excel de carga de cada catálogo*/
 private setExcelRoutes(): Record<string, any> {
    return {
        'suppliers': {
            entityName: 'suppliers',
            columns: [
                { excelColumn: 'Id Interno',               objectKey: 'intern_Id',              required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Razón Social',             objectKey: 'name',                   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Nombre Comercial',         objectKey: 'legal_name',             required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'RFC',                      objectKey: 'tax_id',                 required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Dirección',                objectKey: 'address',                required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Código Postal',            objectKey: 'zip_code',               required: false, transform: (v: any) => this.normalizeText(v) },
                // Carga masiva: cada fila del Excel puede tener UN contacto
                // (Nombre/Teléfono/Correo). El backend lo migra
                // automáticamente al array `contacts` la primera vez que
                // se guarda. Si quieres N contactos por proveedor edita
                // el registro a mano en la UI.
                { excelColumn: 'Contacto',                 objectKey: 'contact_name',           required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Teléfono',                 objectKey: 'contact_phone',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Correo',                   objectKey: 'contact_email',          required: false, transform: (v: any) => this.normalizeEmail(v) },
                { excelColumn: 'Tipo',                     objectKey: 'type',                   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Departamento',             objectKey: 'responsible_department', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Riesgo',                   objectKey: 'risk_classification',    required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Frecuencia de evaluación', objectKey: 'evaluation_frequency',   required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Tiempo de contrato',       objectKey: 'contract_time',          required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Última evaluación',        objectKey: 'last_evaluation_date',   required: false, transform: (v: any) => this.normalizeDate(v) },
                { excelColumn: 'Próxima evaluación',       objectKey: 'next_evaluation_date',   required: false, transform: (v: any) => this.normalizeDate(v) },
                { excelColumn: 'Ejecutivo de cuenta',      objectKey: 'account_executive',      required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Condiciones de pago',      objectKey: 'payment_conditions',     required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Enviar a (ventas)',        objectKey: 'ship_to',                required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Horarios de atención',     objectKey: 'attention_hours',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Divisa de pago',           objectKey: 'payment_currency',       required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',                   objectKey: 'active',                 required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['intern_Id', 'name', 'legal_name', 'tax_id'], allowUpdate: true },
        },
        'brands': {
            entityName: 'brands',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true, transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'manufacturers': {
            entityName: 'manufacturers',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true, transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'paymentMethods': {
            entityName: 'paymentMethods',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Id SAT', objectKey: 'satId',  required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['name', 'satId'], allowUpdate: true },
        },
        'measurementUnits': {
            entityName: 'measurementUnits',
            columns: [
                { excelColumn: 'Nombre',      objectKey: 'name',         required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Abreviación', objectKey: 'abbreviation', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Id SAT',      objectKey: 'satId',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',      objectKey: 'active',       required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['name', 'satId'], allowUpdate: true },
        },
        'measurementInstruments': {
            entityName: 'measurementInstruments',
            columns: [
                { excelColumn: 'Instrumento', objectKey: 'instrument', required: true,  transform: (v: any) => this.normalizeText(v) },
                // La "Marca" del Excel viene como NOMBRE; la resolvemos al id
                // del catálogo de Marcas (igual que proveedor/fabricante en
                // los otros catálogos). Si la marca no existe en el catálogo,
                // la fila se omite y se reporta en "Valores no encontrados".
                { excelColumn: 'Marca',       objectKey: 'brand',      required: false,
                  catalogMapping: { catalog: this.entityLists['brandList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Modelo',      objectKey: 'model',      required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',      objectKey: 'active',     required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['instrument', 'brand', 'model'], allowUpdate: true },
        },
        'medicalGasTanks': {
            entityName: 'medicalGasTanks',
            columns: [
                { excelColumn: 'Código',             objectKey: 'code',             required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Gas',                objectKey: 'gas',              required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Color',              objectKey: 'color',            required: false, transform: (v: any) => { const t = this.normalizeText(v); return t ? this.colorHex(t) : t; } },
                { excelColumn: 'CAS',                objectKey: 'cas',              required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Capacidad',          objectKey: 'capacity',         required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Presentación',       objectKey: 'presentation',     required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Presión de trabajo', objectKey: 'working_pressure', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Registro SSA',       objectKey: 'reg_ssa',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',        objectKey: 'description',       required: false, transform: (v: any) => this.normalizeText(v) },
                // El proveedor del Excel viene como NOMBRE; se resuelve al id.
                { excelColumn: 'Proveedor',          objectKey: 'supplier',          required: false,
                  catalogMapping: { catalog: this.entityLists['supplierList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Activo',             objectKey: 'active',            required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'code', allowUpdate: true },
        },
        'affiliate': {
            entityName: 'affiliates',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true, transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'specialties': {
            entityName: 'specialties',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true, transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'kits': {
            entityName: 'kits',
            columns: [
                { excelColumn: 'Línea',          objectKey: 'line',       required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'COT',            objectKey: 'cot',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'CIR',            objectKey: 'cir',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Código MDT',     objectKey: 'mdtCode',    required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Código EPIONE',  objectKey: 'epioneCode', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Nombre del kit', objectKey: 'name',       required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Cantidad',       objectKey: 'quantity',   required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Activo',         objectKey: 'active',     required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'hospitalServices': {
            entityName: 'hospitalServices',
            // Una fila por producto: se agrupan en un servicio por
            // (CODIGO EPIONE, CIR, NOMBRE DEL SERVICIO).
            groupedConfig: {
                groupByColumns: ['CODIGO EPIONE', 'CIR', 'NOMBRE DEL SERVICIO'],
                arrayObjectKey: 'products',
                arrayItemColumns: [
                    { excelColumn: 'CODIGO DE PRODUCTOS DEL SERVICIO',      objectKey: 'code',        transform: (v: any) => this.upperText(v) },
                    { excelColumn: 'DESCRIPCION DE PRODUCTOS DEL SERVICIO', objectKey: 'description', transform: (v: any) => this.upperText(v) },
                    { excelColumn: 'CANTIDAD MÁX',                          objectKey: 'maxQuantity', transform: (v: any) => this.normalizeNumber(v) },
                    { excelColumn: 'NOTAS',                                 objectKey: 'notes',       transform: (v: any) => this.plainText(v) },
                ],
            },
            columns: [
                { excelColumn: 'ESPECIALIDAD',        objectKey: 'specialty',  required: false,
                  catalogMapping: { catalog: this.entityLists['specialtyList'] ?? [], nameField: 'name', idField: 'id' } },
                // Línea conserva acentos: las opciones del select son 'TÓRAX', 'ENERGÍA'…
                { excelColumn: 'LÍNEA',               objectKey: 'line',       required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'COT',                 objectKey: 'cot',        required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'CIR',                 objectKey: 'cir',        required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'CODIGO MDT',          objectKey: 'mdtCode',    required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'CODIGO EPIONE',       objectKey: 'epioneCode', required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'NOMBRE DEL SERVICIO', objectKey: 'name',       required: true,  transform: (v: any) => this.upperText(v) },
                // Columnas de cada producto: se leen en groupedConfig; aquí
                // solo para que la plantilla de descarga las incluya.
                { excelColumn: 'CODIGO DE PRODUCTOS DEL SERVICIO',      objectKey: '_code',        required: false },
                { excelColumn: 'DESCRIPCION DE PRODUCTOS DEL SERVICIO', objectKey: '_description', required: false },
                { excelColumn: 'CANTIDAD MÁX',                          objectKey: '_maxQuantity', required: false },
                { excelColumn: 'NOTAS',                                 objectKey: '_notes',       required: false },
                { excelColumn: 'ACTIVO',              objectKey: 'active',     required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['epioneCode', 'cir', 'name'], allowUpdate: true },
        },
        // Médicos: una fila por NOMBRE + ALIAS del hospital; se agrupan por
        // nombre y el alias se resuelve al sitio. La hoja ML_BASE_ERRORES
        // (NOMBRE INCORRECTO → NOMBRE CORRECTO) corrige los nombres antes de
        // agrupar y queda guardada en `nameVariants`.
        'doctors': {
            entityName: 'doctors',
            groupedConfig: {
                groupByColumns: ['NOMBRE'],
                arrayColumn: 'ALIAS',
                arrayObjectKey: 'places',
                arrayCatalogMapping: { catalog: this.entityLists['placesList'] ?? [], nameField: 'alias', idField: 'id' },
            },
            preprocess: (rows: any[], sheets: Record<string, any[]>) => this.applyDoctorNameFixes(rows, sheets),
            postprocess: (items: any[], sheets: Record<string, any[]>) => this.attachDoctorNameVariants(items, sheets),
            columns: [
                { excelColumn: 'NOMBRE',       objectKey: 'name',      required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ALIAS',        objectKey: 'places',    required: false },
                { excelColumn: 'ACTIVO',       objectKey: 'active',    required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'places': {
            entityName: 'places',
            columns: [
                { excelColumn: 'NOMBRE',    objectKey: 'name',     required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ALIAS',     objectKey: 'alias',    required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'UBICACIÓN', objectKey: 'location', required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'TIPO',      objectKey: 'type',     required: true,  transform: (v: any) => this.placeTypeFromExcel(v) },
                // Opcionales (no vienen en el archivo de Medtronic).
                { excelColumn: 'ACTIVO',    objectKey: 'active',   required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'institutions': {
            entityName: 'institutions',
            columns: [
                { excelColumn: 'Nombre', objectKey: 'name',   required: true, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo', objectKey: 'active', required: true, transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'clients': {
            entityName: 'clients',
            columns: [
                { excelColumn: 'Nombre',         objectKey: 'name',          required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Razón Social',   objectKey: 'legal_name',    required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'RFC',            objectKey: 'tax_id',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Régimen Fiscal', objectKey: 'tax_regime',    required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Dirección',      objectKey: 'address',       required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Código Postal',  objectKey: 'zip_code',      required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Contacto',       objectKey: 'contact_name',  required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Teléfono',       objectKey: 'contact_phone', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Correo',         objectKey: 'contact_email', required: false, transform: (v: any) => this.normalizeEmail(v) },
                { excelColumn: 'Tipo',           objectKey: 'type',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',         objectKey: 'active',        required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['name', 'legal_name', 'tax_id'], allowUpdate: true },
        },
        'warehouses': {
            entityName: 'warehouses',
            // El tipo se deriva del tipo del sitio (las actualizaciones por
            // Excel van por PATCH múltiple y no pasan por los hooks de la API).
            // TIPO: Locker | Inhouse (vacío → Locker).
            postprocess: (items: any[]) => items.map(it => ({
              ...it,
              type: this.warehouseTypeForPlace(it.place) ?? it.type,
              storageType: /in\s*-?\s*house/i.test(String(it.storageType ?? '')) ? 'INHOUSE' : 'LOCKER',
            })),
            columns: [
                // El sitio se liga por su ALIAS (catálogo de Sitios).
                // Solo alias de sitios tipo Hospital; otro alias no se reconoce.
                { excelColumn: 'ALIAS',     objectKey: 'place',    required: true, excelColumnAliases: ['Sitio'],
                  catalogMapping: { catalog: this.hospitalPlaces(), nameField: 'alias', idField: 'id' } },
                { excelColumn: 'UBICACIÓN', objectKey: 'location', required: false, excelColumnAliases: ['UBICACION'], transform: (v: any) => this.upperText(v) },
                { excelColumn: 'NOMBRE',    objectKey: 'name',     required: true,  excelColumnAliases: ['Nombre'], transform: (v: any) => this.upperText(v) },
                { excelColumn: 'TIPO',      objectKey: 'storageType', required: false, excelColumnAliases: ['Tipo'] },
                { excelColumn: 'ACTIVO',    objectKey: 'active',   required: false, excelColumnAliases: ['Activo'], transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['name', 'place'], allowUpdate: true },
        },
        'instrumentals': {
            entityName: 'instrumentals',
            columns: [
                { excelColumn: 'ESPECIALIDAD', objectKey: 'specialty',    required: false,
                  catalogMapping: { catalog: this.entityLists['specialtyList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'NOMBRE',       objectKey: 'name',         required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'DESCRIPCIÓN',  objectKey: 'description',  required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'FABRICANTE',   objectKey: 'manufacturer', required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'ALIAS',        objectKey: 'place',        required: false,
                  catalogMapping: { catalog: this.entityLists['placesList'] ?? [], nameField: 'alias', idField: 'id' } },
                { excelColumn: 'ACTIVO',       objectKey: 'active',       required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['name', 'place'], allowUpdate: true },
        },
        'spareParts': {
            entityName: 'spareParts',
            columns: [
                { excelColumn: 'Número de Parte',    objectKey: 'part_number',    required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',        objectKey: 'description',    required: false, transform: (v: any) => this.normalizeText(v) },
                // La Marca viene como NOMBRE; se resuelve al id del catálogo.
                { excelColumn: 'Marca',              objectKey: 'brand',          required: false,
                  catalogMapping: { catalog: this.entityLists['brandList'] ?? [],          nameField: 'name', idField: 'id' } },
                { excelColumn: 'Proveedor',          objectKey: 'supplier',       required: false,
                  catalogMapping: { catalog: this.entityLists['supplierList'] ?? [],     nameField: 'intern_Id', idField: 'id' } },
                { excelColumn: 'Fabricante',         objectKey: 'manufacturer',   required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Equipo',             objectKey: 'equipment',      required: false,
                  catalogMapping: { catalog: this.entityLists['equipmentList'] ?? [],    nameField: 'code', idField: 'id' } },
                { excelColumn: 'Clasificación',      objectKey: 'classification', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Especialidad',       objectKey: 'specialty',      required: false,
                  catalogMapping: { catalog: this.entityLists['specialtyList'] ?? [],    nameField: 'name', idField: 'id' } },
                { excelColumn: 'Vida de Servicio',   objectKey: 'service_life',   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Inventario Mínimo',  objectKey: 'min_stock',      required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Inventario Máximo',  objectKey: 'max_stock',      required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Prioridad',          objectKey: 'priority_level', required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',             objectKey: 'active',         required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'part_number', allowUpdate: true },
        },
        'products': {
            entityName: 'products',
            columns: [
                { excelColumn: 'Código',             objectKey: 'code',         required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',        objectKey: 'description',  required: true,  transform: (v: any) => this.normalizeText(v) },
                // El Fabricante viene como NOMBRE; se resuelve al id del catálogo.
                { excelColumn: 'Fabricante',         objectKey: 'manufacturer', required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                // Solo SES / EMID / STI / OTROS (cualquier otro valor se ignora).
                { excelColumn: 'Grupo de productos', objectKey: 'productGroup', required: false, transform: (v: any) => this.productGroupFromExcel(v) },
                { excelColumn: 'ALG',                objectKey: 'alg',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Unidad de Medida',   objectKey: 'UOM',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Precio',             objectKey: 'price',        required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Activo',             objectKey: 'active',       required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'code', allowUpdate: true },
        },
        'inhouses': {
            entityName: 'inhouses',
            columns: [
                { excelColumn: 'NOMBRE',           objectKey: 'name',     required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ALIAS (HOSPITAL)', objectKey: 'place',    required: true,  excelColumnAliases: ['ALIAS', 'HOSPITAL'],
                  catalogMapping: { catalog: this.hospitalPlaces(), nameField: 'alias', idField: 'id' } },
                { excelColumn: 'UBICACIÓN',        objectKey: 'location', required: false, excelColumnAliases: ['UBICACION'], transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ACTIVO',           objectKey: 'active',   required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['name', 'place'], allowUpdate: true },
        },
        'rfidDevices': {
            entityName: 'rfidDevices',
            // El locker se resuelve por (ubicación, nombre): el nombre del locker se repite entre hospitales.
            postprocess: (items: any[]) => items.map(({ _lockerName, ...it }) => ({ ...it, warehouse: this.lockerIdFor(it.place, _lockerName) })),
            columns: [
                { excelColumn: 'NOMBRE',            objectKey: 'name',         required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'TIPO',              objectKey: 'type',         required: true,  transform: (v: any) => rfidTypeFromText(v) },
                { excelColumn: 'PROVEEDOR',         objectKey: 'provider',     required: false, transform: (v: any) => rfidProviderFromText(v) },
                { excelColumn: 'ALIAS (UBICACIÓN)', objectKey: 'place',        required: true,  excelColumnAliases: ['ALIAS', 'UBICACIÓN', 'UBICACION'],
                  catalogMapping: { catalog: this.hospitalPlaces(), nameField: 'alias', idField: 'id' } },
                { excelColumn: 'LOCKER',            objectKey: '_lockerName',  required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'NO. DE SERIE',      objectKey: 'serialNumber', required: false, excelColumnAliases: ['NO DE SERIE', 'SERIE'], transform: (v: any) => this.upperText(v) },
                { excelColumn: 'RESPONSABLE',       objectKey: 'responsible',  required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ACTIVO',            objectKey: 'active',       required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['name', 'place'], allowUpdate: true },
        },
        'equipments': {
            entityName: 'equipments',
            columns: [
                { excelColumn: 'NOMBRE DE EQUIPO',       objectKey: 'name',        required: true,  transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ALIAS',                  objectKey: 'place',       required: true,
                  catalogMapping: { catalog: this.entityLists['placesList'] ?? [], nameField: 'alias', idField: 'id' } },
                { excelColumn: 'RESPONSABLE DEL EQUIPO', objectKey: 'responsible', required: false, transform: (v: any) => this.upperText(v) },
                { excelColumn: 'ACTIVO',                 objectKey: 'active',      required: false, transform: (v: any) => this.activeOrTrue(v) },
            ],
            validation: { targetColumn: ['name', 'place'], allowUpdate: true },
        },
        // ── ACCESORIOS ──────────────────────────────────────────────────
        // Mismas columnas que la tabla. "Equipos ligados" acepta uno o
        // varios CÓDIGOS de equipo separados por coma → arreglo de ids.
        'accessories': {
            entityName: 'accessories',
            columns: [
                { excelColumn: 'Código',               objectKey: 'code',                  required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',          objectKey: 'description',           required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Modelo',               objectKey: 'model',                 required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción Factura',  objectKey: 'invoice_description',   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Marca',                objectKey: 'brand',                 required: false,
                  catalogMapping: { catalog: this.entityLists['brandList'] ?? [],        nameField: 'name', idField: 'id' } },
                { excelColumn: 'Proveedor',            objectKey: 'supplier',              required: false,
                  catalogMapping: { catalog: this.entityLists['supplierList'] ?? [],     nameField: 'intern_Id', idField: 'id' } },
                { excelColumn: 'Fabricante',           objectKey: 'manufacturer',          required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Especialidad',         objectKey: 'specialty',             required: false,
                  excelColumnAliases: ['Coordinación', 'Coordinacion'],
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['specialtyList'] ?? [], 'name') },
                { excelColumn: 'Equipos ligados',      objectKey: 'equipments',            required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['equipmentList'] ?? [], 'code') },
                { excelColumn: 'Activo',               objectKey: 'active',                required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'code', allowUpdate: true },
        },
        // ── EQUIPO PERIFÉRICO ───────────────────────────────────────────
        // "Equipo ligado" acepta el CÓDIGO del equipo → id.
        'peripheralEquipment': {
            entityName: 'peripheralEquipment',
            columns: [
                { excelColumn: 'Código',               objectKey: 'code',                  required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Modelo',               objectKey: 'model',                 required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción Factura',  objectKey: 'invoice_description',   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',          objectKey: 'description',           required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Marca',                objectKey: 'brand',                 required: false,
                  catalogMapping: { catalog: this.entityLists['brandList'] ?? [],        nameField: 'name', idField: 'id' } },
                { excelColumn: 'Proveedor',            objectKey: 'supplier',              required: false,
                  catalogMapping: { catalog: this.entityLists['supplierList'] ?? [],     nameField: 'intern_Id', idField: 'id' } },
                { excelColumn: 'Fabricante',           objectKey: 'manufacturer',          required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Equipo ligado',        objectKey: 'equipment',             required: false,
                  catalogMapping: { catalog: this.entityLists['equipmentList'] ?? [],    nameField: 'code', idField: 'id' } },
                { excelColumn: 'Frecuencia de Mantenimiento', objectKey: 'pm_frequency',   required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',               objectKey: 'active',                required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'code', allowUpdate: true },
        },
        // ── CONFIGURACIÓN DE ACTIVO FIJO ────────────────────────────────
        'fixedAssetConfigurations': {
            entityName: 'fixedAssetConfigurations',
            columns: [
                { excelColumn: 'Nombre',               objectKey: 'name',                  required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Equipo',               objectKey: 'equipment',             required: false,
                  catalogMapping: { catalog: this.entityLists['equipmentList'] ?? [],    nameField: 'code', idField: 'id' } },
                { excelColumn: 'Descripción',          objectKey: 'description',           required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Accesorios',           objectKey: 'accessories',           required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['accessoriesList'] ?? [], 'code') },
                { excelColumn: 'Periféricos',          objectKey: 'peripheral_equipment',  required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['peripheralEquipmentList'] ?? [], 'code') },
                { excelColumn: 'Tanques de Gas',       objectKey: 'gas_tanks',             required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['medicalGasTanksList'] ?? [], 'code') },
                { excelColumn: 'Activo',               objectKey: 'active',                required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        // ── CONFIGURACIÓN DE SERVICIO DE ACTIVO FIJO ────────────────────
        'fixedAssetServiceConfigurations': {
            entityName: 'fixedAssetServiceConfigurations',
            columns: [
                { excelColumn: 'Nombre',               objectKey: 'name',                  required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Equipo',               objectKey: 'equipment',             required: false,
                  catalogMapping: { catalog: this.entityLists['equipmentList'] ?? [],    nameField: 'code', idField: 'id' } },
                { excelColumn: 'Tipo de Servicio',     objectKey: 'service_type',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',          objectKey: 'description',           required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Instrumental de Medición', objectKey: 'measurement_instruments', required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['measurementInstrumentsList'] ?? [], 'instrument') },
                { excelColumn: 'Refacciones',          objectKey: 'spare_parts',           required: false,
                  transform: (v: any) => this.mapNamesToIds(v, this.entityLists['sparePartsList'] ?? [], 'part_number') },
                { excelColumn: 'Activo',               objectKey: 'active',                required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        // ── INSUMOS ─────────────────────────────────────────────────────
        'supplies': {
            entityName: 'supplies',
            columns: [
                { excelColumn: 'Código',            objectKey: 'code',         required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción',       objectKey: 'description',  required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Modelo',            objectKey: 'model',        required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Marca',             objectKey: 'brand',        required: false,
                  catalogMapping: { catalog: this.entityLists['brandList'] ?? [],        nameField: 'name', idField: 'id' } },
                { excelColumn: 'Proveedor',         objectKey: 'supplier',     required: false,
                  catalogMapping: { catalog: this.entityLists['supplierList'] ?? [],     nameField: 'intern_Id', idField: 'id' } },
                { excelColumn: 'Fabricante',        objectKey: 'manufacturer', required: false,
                  catalogMapping: { catalog: this.entityLists['manufacturerList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Unidad de Medida',  objectKey: 'UOM',          required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Código UDM',        objectKey: 'UOM_code',     required: false, transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Inventario Mínimo', objectKey: 'min_stock',    required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Inventario Máximo', objectKey: 'max_stock',    required: false, transform: (v: any) => this.normalizeNumber(v) },
                { excelColumn: 'Activo',            objectKey: 'active',       required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'code', allowUpdate: true },
        },
        'procedures': {
            entityName: 'procedures',
            columns: [
                { excelColumn: 'Cirugía',      objectKey: 'name',     required: true,  excelColumnAliases: ['Nombre', 'Cirugia'], transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Especialidad', objectKey: 'specialty', required: false,
                  catalogMapping: { catalog: this.entityLists['specialtyList'] ?? [], nameField: 'name', idField: 'id' } },
                { excelColumn: 'Activo',       objectKey: 'active',   required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: 'name', allowUpdate: true },
        },
        'taxRegimes': {
            entityName: 'taxRegimes',
            columns: [
                { excelColumn: 'Clave',       objectKey: 'code',        required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Descripción', objectKey: 'description', required: true,  transform: (v: any) => this.normalizeText(v) },
                { excelColumn: 'Activo',      objectKey: 'active',      required: true,  transform: (v: any) => this.transformToBoolean(v) },
            ],
            validation: { targetColumn: ['code', 'description'], allowUpdate: true },
        },
    };
  }

  private callAnyEntityService(entity: string): BaseService | undefined{
    const serviceMap: Record<string, BaseService> = {
      brand:        this.brandService,
      client:       this.clientService,
      equipment:    this.equipmentsService,
      affiliate:    this.affiliateService,
      instrumental: this.instrumentalService,
      manufacturer: this.manufacturerService,
      specialty:    this.specialtyService,
      supplier:     this.supplierService,
      affiliates:    this.affiliateService,
      brands:        this.brandService,
      clients:       this.clientService,
      equipments:    this.equipmentsService,
      rfidDevices:   this.rfidDevicesService,
      inhouses:      this.inhousesService,
      instrumentals: this.instrumentalService,
      manufacturers: this.manufacturerService,
      measurementInstrument:  this.measurementInstrumentsService,
      measurementInstruments: this.measurementInstrumentsService,
      medicalGasTanks:        this.medicalGasTanksService,
      measurementUnits: this.measurementUnitsService,
      paymentMethods:   this.paymentMethodsService,
      places:        this.placesService,
      procedure:     this.procedureService,
      procedures:    this.procedureService,
      products:      this.productsService,
      spareParts:    this.sparePartsService,
      specialties:   this.specialtyService,
      suppliers:     this.supplierService,
      warehouses:    this.warehousesService,
      hospitalService:  this.hospitalServicesService,
      hospitalServices: this.hospitalServicesService,
      kits:             this.kitService,
      courses:       this.coursesService,
      roles:         this.rolesService,
      taxRegimes:          this.taxRegimesService,
      accessories:         this.accessoriesService,
      accessory:           this.accessoriesService,
      peripheralEquipment: this.peripheralEquipmentService,
      fixedAssetConfigurations:        this.fixedAssetConfigurationsService,
      fixedAssetServiceConfigurations: this.fixedAssetServiceConfigurationsService,
      supplies:            this.suppliesService,
      institution:         this.institutionsService,
      institutions:        this.institutionsService,
      doctor:              this.doctorsService,
      doctors:             this.doctorsService,
      substitutes:         this.substitutesService,
    };
    return serviceMap[entity];
  }

  async searchAnyEntity(entity: string, groupKey?:string){
    try{
      const service = this.callAnyEntityService(entity);
      if (!service) {
        // Entidad sin servicio registrado: se omite en vez de romper la
        // carga de todo el catálogo.
        console.warn(`[searchAnyEntity] sin servicio para '${entity}', se omite`);
        return;
      }
      const apiResponse = await service.search({searchType:'default',condition:{deleted:false}})
      if (apiResponse?.state === 'success') {
        this.entityLists[entity+'List'] = apiResponse.data;
        if(entity === 'doctor' || entity === 'stages')this.entityLists[entity+'List'] = this.entityLists[entity+'List'].filter((x: { status: boolean; }) => x.status !== false)
        this.entityLists[entity+'List'] = this.applyAlphabeticSorting(this.entityLists[entity+'List'],groupKey??'name')
        if(entity === 'products'){
          this.entityObjects[entity+'Object'] = this.entityLists[entity+'List'].reduce(function (r: { [x: string]: { code: any; description: any; itemNumber: any; usageFactor: any; }; }, a: { [x: string]: any; }) {
            r[a['id']] = (r[a['id']] || []) as any;
            r[a['id']] = {
              'code': a['code'],
              'description': a['description'],
              'itemNumber': a['itemNumber'],
              'usageFactor': a['usageFactor']
            };
            return r;
          }, Object.create(null));
        }else if (entity === 'equipment') {
    // Equipment no tiene `name`, tiene `code`. Construimos el objeto
    // reductor usando description para mostrar algo identificable.
    this.entityObjects[entity+'Object'] = this.entityLists[entity+'List'].reduce((r: any, a: any) => {
        r[a['id']] = a['code'] && a['description']
            ? `${a['code']} — ${a['description']}`
            : (a['code'] ?? a['description'] ?? '');
        return r;
    }, Object.create(null));
}       else{
          this.entityObjects[entity+'Object'] = this.entityLists[entity+'List'].reduce(function (r: { [x: string]: any; }, a: { [x: string]: any; }) {
            r[a['id']] = r[a['id']] || [];
            r[a['id']] = a[groupKey??'name'];
            return r;
          }, Object.create(null));
        }
      }
    }catch(err){
      throw err;
    }
  }

  /**
   * Resuelve los IDs de cada registro a su valor legible. La regla
   * general es `_<campo>` ← nombre de la entidad referenciada.
   *
   * Excepción intencional: para los catálogos donde mostramos
   * "INTERN_ID — Nombre" en la columna proveedor (equipments,
   * instrumentals, spareParts, products), el `_supplier` se construye
   * con el helper `formatSupplierLabel` en vez de tomar solo el nombre.
   */
  changeIds(response: any[]){
    response.forEach(element=>{element._active = element.active ? 'Activo' : 'Inactivo'})

    // Firma las columnas de imagen (logo de filiales, imagen de equipos…)
    // para que la miniatura de la tabla se pueda mostrar. Va sin await: la
    // tabla se pinta de inmediato y las miniaturas aparecen al resolverse.
    void this.resolveImageColumns(response);

    // Helper local: resuelve un supplier id a "INTERN_ID — Nombre"
    // buscando en la lista cruda de suppliers cargada en entityLists.
    const supplierLabel = (supplierId: string): string => {
      if (!supplierId) return '';
      const s = (this.entityLists?.['supplierList'] ?? [])
        .find((x: any) => x?.id === supplierId);
      return formatSupplierLabel(s);
    };

    // Homologación visual: en los catálogos donde el negocio exige que los
    // nombres se vean SIEMPRE en mayúsculas, normalizamos el texto que se
    // pinta en la tabla (los registros históricos se capturaron en minúsculas
    // antes de que el diálogo forzara mayúsculas).
    // `UOM_code` es id del catálogo de Unidades de Medida en Insumos, pero en
    // Consumibles sigue siendo texto libre. Resolvemos de forma global con
    // respaldo al texto original para que ninguna tabla quede en blanco.
    response.forEach(element => {
      if (element?.UOM_code === undefined) return;
      const resolved = this.entityObjects?.['measurementUnitsObject']?.[element.UOM_code];
      element._UOM_code = resolved ?? (typeof element.UOM_code === 'string' ? element.UOM_code : '');
    });

    if (this.route === 'suppliers') {
      const upper = (v: any) => (v ?? '') === '' ? v : String(v).toUpperCase();
      response.forEach(element => {
        element.name       = upper(element.name);
        element.legal_name = upper(element.legal_name);
        element.tax_id     = upper(element.tax_id);
      });
    }

    switch (this.route) {
      case 'clients':
          response.forEach(element => {
            const regimes: any[] = this.entityLists?.['taxRegimesList'] ?? [];
            const r = regimes.find((x: any) => x?.id === element.tax_regime);
            element._tax_regime = r
              ? `${r.code} — ${r.description}`
              : (element.tax_regime ?? '');
          });
          break;
      case 'instrumentals':
          response.forEach(element =>{
            element._specialty    = this.entityObjects?.['specialtyObject']?.[element.specialty]       ?? '';
            element._manufacturer = this.entityObjects?.['manufacturerObject']?.[element.manufacturer] ?? '';
            element._place        = this.placeAlias(element.place);
          })
          break;
      case 'spareParts':
          response.forEach(element =>{
            element._specialty    = this.entityObjects?.['specialtyObject']?.[element.specialty]       ?? '';
            element._manufacturer = this.entityObjects?.['manufacturerObject']?.[element.manufacturer] ?? '';
            element._equipment    = this.entityObjects?.['equipmentObject']?.[element.equipment]       ?? '';
            element._brand        = this.entityObjects?.['brandObject']?.[element.brand]               ?? '';
            element._supplier     = supplierLabel(element.supplier);
          })
          break;
      case 'products':
          // Rediseño: la tabla solo resuelve el fabricante; el resto de
          // columnas (grupo, ALG, UDM, precio) son valores directos.
          response.forEach(element =>{
            element._manufacturer = this.entityObjects?.['manufacturerObject']?.[element.manufacturer] ?? '';
          })
          break;
      case 'equipments':
          // Mismas columnas que el archivo: el sitio se muestra por su alias.
          response.forEach(element =>{
            element._place = this.placeAlias(element.place);
          })
          break;
      case 'accessories':
      case 'peripheralEquipment':
          response.forEach(element =>{
            const specialtyIds: any[] = Array.isArray(element.specialty)
              ? element.specialty
              : (element.specialty ? [element.specialty] : []);
            element.specialty = specialtyIds;
            element._specialty = specialtyIds
              .map(id => this.entityObjects?.['specialtyObject']?.[id] ?? '')
              .filter((txt: string) => !!txt)
              .join(', ');
            const miList: any[] = this.entityLists?.['measurementInstrumentsList'] ?? [];
            const miIds: any[] = Array.isArray(element.measurement_instruments)
              ? element.measurement_instruments
              : (element.measurement_instruments ? [element.measurement_instruments] : []);
            element.measurement_instruments = miIds;
            element._measurement_instruments = miIds
              .map(id => {
                const mi = miList.find((x: any) => x?.id === id);
                if (!mi) return '';
                return mi.instrument ? `${mi.instrument}${mi.model ? ' · ' + mi.model : ''}` : (mi.model ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
            element._manufacturer = this.entityObjects?.['manufacturerObject']?.[element.manufacturer] ?? '';
            element._brand        = this.entityObjects?.['brandObject']?.[element.brand]               ?? '';
            element._supplier     = supplierLabel(element.supplier);
            element._equipment    = this.entityObjects?.['equipmentObject']?.[element.equipment]       ?? '';
            // Accesorios: `equipments` (Equipos ligados) es una LISTA de ids
            // del catálogo de Equipos → texto legible "COD — Descripción, ...".
            const eqLinkedIds: any[] = Array.isArray(element.equipments)
              ? element.equipments
              : (element.equipments ? [element.equipments] : []);
            element.equipments = eqLinkedIds;
            const eqCatalog: any[] = this.entityLists?.['equipmentList'] ?? [];
            element._equipments = eqLinkedIds
              .map(id => formatEquipmentLabel(eqCatalog.find((x: any) => x?.id === id)))
              .filter((txt: string) => !!txt)
              .join(', ');
            element._ssa_reg_holder = element.ssa_reg_holder ? 'Sí' : 'No';
          })
          break;
      case 'fixedAssetConfigurations':
          response.forEach(element => {
            element._equipment = this.entityObjects?.['equipmentObject']?.[element.equipment] ?? '';
            const accList: any[] = this.entityLists?.['accessoriesList'] ?? [];
            const accIds: any[] = Array.isArray(element.accessories) ? element.accessories : (element.accessories ? [element.accessories] : []);
            element.accessories = accIds;
            element._accessories = accIds
              .map(id => {
                const a = accList.find((x: any) => x?.id === id);
                if (!a) return '';
                return a.code ? `${a.code}${a.description ? ' · ' + a.description : ''}` : (a.description ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
            const perList: any[] = this.entityLists?.['peripheralEquipmentList'] ?? [];
            const perIds: any[] = Array.isArray(element.peripheral_equipment) ? element.peripheral_equipment : (element.peripheral_equipment ? [element.peripheral_equipment] : []);
            element.peripheral_equipment = perIds;
            element._peripheral_equipment = perIds
              .map(id => {
                const p = perList.find((x: any) => x?.id === id);
                if (!p) return '';
                return p.code ? `${p.code}${p.description ? ' · ' + p.description : ''}` : (p.description ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
            const gasList: any[] = this.entityLists?.['medicalGasTanksList'] ?? [];
            const gasIds: any[] = Array.isArray(element.gas_tanks) ? element.gas_tanks : (element.gas_tanks ? [element.gas_tanks] : []);
            element.gas_tanks = gasIds;
            element._gas_tanks = gasIds
              .map(id => {
                const g = gasList.find((x: any) => x?.id === id);
                if (!g) return '';
                return g.code ? `${g.code}${g.gas ? ' · ' + g.gas : ''}` : (g.gas ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
          });
          break;
      case 'fixedAssetServiceConfigurations':
          response.forEach(element => {
            element._equipment = this.entityObjects?.['equipmentObject']?.[element.equipment] ?? '';
            const miList: any[] = this.entityLists?.['measurementInstrumentsList'] ?? [];
            const miIds: any[] = Array.isArray(element.measurement_instruments) ? element.measurement_instruments : (element.measurement_instruments ? [element.measurement_instruments] : []);
            element.measurement_instruments = miIds;
            element._measurement_instruments = miIds
              .map(id => {
                const mi = miList.find((x: any) => x?.id === id);
                if (!mi) return '';
                return mi.instrument ? `${mi.instrument}${mi.model ? ' · ' + mi.model : ''}` : (mi.model ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
            const spList: any[] = this.entityLists?.['sparePartsList'] ?? [];
            const spIds: any[] = Array.isArray(element.spare_parts) ? element.spare_parts : (element.spare_parts ? [element.spare_parts] : []);
            element.spare_parts = spIds;
            element._spare_parts = spIds
              .map(id => {
                const s = spList.find((x: any) => x?.id === id);
                if (!s) return '';
                return s.part_number ? `${s.part_number}${s.description ? ' · ' + s.description : ''}` : (s.description ?? '');
              })
              .filter((txt: string) => !!txt)
              .join(', ');
          });
          break;
      case 'supplies':
          response.forEach(element => {
            element._brand        = this.entityObjects?.['brandObject']?.[element.brand]               ?? '';
            element._manufacturer = this.entityObjects?.['manufacturerObject']?.[element.manufacturer] ?? '';
            element._supplier     = supplierLabel(element.supplier);
          });
          break;
      case 'medicalGasTanks':
          response.forEach(element => {
            element._supplier = supplierLabel(element.supplier);
            element._equipment = this.entityObjects?.['equipmentObject']?.[element.equipment] ?? '';
          });
          break;
      case 'measurementInstruments':
          // El campo `brand` ahora guarda el id del catálogo de Marcas.
          // Lo resolvemos a nombre legible para mostrar en la tabla.
          // Fallback: si el registro es viejo y aún guarda un string
          // libre (ej. "Fluke"), lo mostramos tal cual para no romper
          // datos históricos.
          response.forEach(element => {
            const resolved = this.entityObjects?.['brandObject']?.[element.brand];
            element._brand = resolved ?? (typeof element.brand === 'string' ? element.brand : '');
            // `equipments` es una LISTA de ids del catálogo de Equipos.
            // Compatibilidad: los registros viejos guardaban un solo id en
            // `equipment`, así que se absorbe en la lista.
            const eqList: any[] = this.entityLists?.['equipmentList'] ?? [];
            let miEqIds: any[] = Array.isArray(element.equipments)
              ? element.equipments
              : (element.equipments ? [element.equipments] : []);
            if (!miEqIds.length && element.equipment) miEqIds = [element.equipment];
            element.equipments = miEqIds;
            element._equipments = miEqIds
              .map(id => formatEquipmentLabel(eqList.find((x: any) => x?.id === id)))
              .filter((txt: string) => !!txt)
              .join(', ');
          });
          break;
      case 'warehouses':
          response.forEach(element =>{
            element._place = this.placeAlias(element.place);
          })
          break;
      case 'inhouses':
          response.forEach(element => { element._place = this.placeAlias(element.place); });
          break;
      case 'rfidDevices':
          response.forEach(element => {
            element._rfidType = RFID_DEVICE_TYPES.find(t => t.id === element.type)?.name ?? element.type ?? '';
            element._place = this.placeAlias(element.place);
            element._warehouse = this.entityObjects?.['warehousesObject']?.[element.warehouse] ?? (element.warehouse ? '—' : 'Sin locker');
          });
          break;
      case 'procedures':
          response.forEach(element =>{
            element._specialty = this.entityObjects?.['specialtyObject']?.[element.specialty] ?? '';
          })
          break;
      case 'doctors':
          // Especialidad (id) y hospitales (lista de ids → alias).
          response.forEach(element => {
            element._specialty = this.entityObjects?.['specialtyObject']?.[element.specialty] ?? '';
            const placeIds: any[] = Array.isArray(element.places) && element.places.length
              ? element.places
              : (element.place ? [element.place] : []);
            element.places = placeIds;
            element._places = placeIds.map(id => this.placeAlias(id)).filter(t => !!t).join(', ');
            element._nameVariants = (Array.isArray(element.nameVariants) ? element.nameVariants : []).join(', ');
          });
          break;
      case 'substitutes':
          // Par producto → sustituto: ambos son ids del catálogo de
          // productos; en tabla se muestra su CÓDIGO (con descripción corta).
          response.forEach(element => {
            const productos: any[] = this.entityLists?.['productsList'] ?? [];
            const codigo = (id: any) => {
              const prod = productos.find((p: any) => p?.id === id);
              if (!prod) return '';
              const desc = (prod.description ?? '').toString().trim();
              return desc ? `${prod.code} — ${desc}` : (prod.code ?? '');
            };
            element._productId    = codigo(element.productId);
            element._substituteId = codigo(element.substituteId);
          });
          break;
      case 'hospitalServices':
          // `products` son renglones {code, description, maxQuantity, notes}
          // (los registros viejos traen ids del catálogo: se convierten).
          response.forEach(element => {
            element._specialty = this.entityObjects?.['specialtyObject']?.[element.specialty] ?? '';
            const productos: any[] = this.entityLists?.['productsList'] ?? [];
            const raw: any[] = Array.isArray(element.products) ? element.products : [];
            const lines = raw.filter(x => !!x).map(item => {
              if (typeof item === 'object') return item;
              const prod = productos.find((p: any) => p?.id === item);
              return { code: prod?.code ?? null, description: prod?.description ?? null, maxQuantity: null, notes: null, product: item };
            });
            element.products = lines;
            element._products = lines.map(l => l.code ?? '').join(', ');
            element._productDescriptions = lines.map(l => (l.description ?? '').toString().trim()).join(', ');
            element._productQuantities = lines.map(l => l.maxQuantity ?? '—').join(', ');
            element._productNotes = lines.map(l => l.notes).filter(n => !!n).join(' | ');
          });
          break;
      case 'courses':
          // `roles` es un arreglo de IDs del catálogo de Roles. Lo
          // resolvemos a una lista legible de nombres separados por coma.
          response.forEach(element => {
            const ids: any[] = Array.isArray(element.roles) ? element.roles : [];
            element._roles = ids
              .map(id => this.entityObjects?.['rolesObject']?.[id] ?? '')
              .filter((txt: string) => !!txt)
              .join(', ');
            // Equipos médicos relacionados (capacitación técnica): array de ids.
            const eqIds: any[] = Array.isArray(element.equipment) ? element.equipment : [];
            element._equipment = eqIds
              .map(id => this.entityObjects?.['equipmentObject']?.[id] ?? '')
              .filter((txt: string) => !!txt)
              .join(', ');
          });
          break;
      case 'places':
          // Construimos un resumen legible de los servicios asociados al
          // sitio. Mostramos "Servicio (sub dirección)" separados por
          // coma, o sólo el nombre del servicio si no hay sub dirección.
          response.forEach(element => {
            // Tipo siempre en MAYÚSCULAS (los registros viejos venían como
            // 'Hospital' / 'Almacen local'); así coincide con el select al editar.
            if (element.type) element.type = String(element.type).trim().toUpperCase();
            // Cliente al que pertenece el sitio (sólo aplica a tipo Hospital).
            // El campo se llama `institution` por compatibilidad; ahora se
            // resuelve contra CLIENTES, con respaldo al catálogo de
            // Instituciones para los registros capturados antes del cambio.
            element._institution = this.entityObjects?.['clientObject']?.[element.institution]
                                ?? this.entityObjects?.['institutionsObject']?.[element.institution]
                                ?? '';
            const items: any[] = Array.isArray(element.services) ? element.services : [];
            element._services = items
              .map(item => {
                const serviceName = this.entityObjects?.['hospitalServicesObject']?.[item?.service] ?? '';
                const sub = item?.subDireccion ? ` (${item.subDireccion})` : '';
                return serviceName ? `${serviceName}${sub}` : '';
              })
              .filter(txt => !!txt)
              .join(', ');
          });
          break;
      case 'suppliers':
          // `contacts` es un array de { name, phone, email }. Si el
          // registro viejo no lo tiene pero sí trae los campos sueltos,
          // los normalizamos a un único contacto en memoria — así al
          // editar el dialog ya muestra una fila precargada y al
          // guardar el backend escribe la nueva estructura.
          response.forEach(element => {
            if (!Array.isArray(element.contacts) || element.contacts.length === 0) {
              if (element.contact_name || element.contact_phone || element.contact_email) {
                element.contacts = [{
                  name:  element.contact_name  ?? '',
                  phone: element.contact_phone ?? '',
                  email: element.contact_email ?? '',
                }];
              } else {
                element.contacts = [];
              }
            }
            // Resumen legible para la columna de la tabla
            element._contacts = (element.contacts as any[])
              .map(c => {
                const parts = [c?.name, c?.phone, c?.email].filter((p: any) => !!p && String(p).trim() !== '');
                return parts.join(' / ');
              })
              .filter(txt => !!txt)
              .join(' • ');
          });
          break;
      default:
          break;
    }
    return response
  }

  /* ************************************ uiData ***************************************************** */
  async getEntityData(){
    let response: any = this.setRoutes()[this.route];
    let apiResponse: any;
    if(response){
      if('entities' in response){
        this.customMessageService.open({title: "Espere",type: 'neutral', message: 'Cargando información...', loading: true});
        for(const entity of response.entities ){
          try{
            await this.searchAnyEntity(entity);
          }catch(err){
            this.customMessageService.close();
            await this.customMessageService.open({title: 'Error', type: 'error', message: 'Error al cargar los datos iniciales: ' + err, dismissBtn: true});
          }
        }
      }
      this.dataService = response.dataService;
      // Mismo nombre que la tarjeta del menú y el menú lateral: la etiqueta del
      // módulo; si no hay, el nombre de respaldo de catalogue-titles.ts.
      this.catalogue = this.moduleLabel || catalogueTitle(this.route, response.catalogue);
      this.cat = response.cat;
      // Si el usuario tiene permiso de editar o borrar, anteponemos
      // la columna de checkboxes para selección múltiple. Se mete en
      // `keys` (no en `keyNames`) porque el HTML referencia keyNames
      // por índice del @for. Para 'select' devolvemos '' en el header.
      const enableBulk = this.permissions.update || this.permissions.delete;
      this.keys = enableBulk ? ['select', ...response.keys] : response.keys;
      this.keyNames = enableBulk ? ['', ...response.keyNames] : response.keyNames;
      this.inputs = response.inputs;
      this.selection.clear();
      try{
        apiResponse = await this.callAnyEntityService(response.dataService)?.search({searchType:'default',condition:{deleted:false}})
      }catch(err){
        this.customMessageService.close();
        await this.customMessageService.open({title: 'Error', type: 'error', message: 'Error al cargar datos', dismissBtn: true, dismissBtnText: 'Cerrar' });
      }
      this.createBtn = true;

      if (!apiResponse?.data || apiResponse.data[0] === null) {
        this.customMessageService.close();
        await this.customMessageService.open({title: 'Información', type: 'neutral', message: 'No se encontraron registros', duration: 2000 });
      }else{
        let fixedResponse = this.changeIds(apiResponse['data'])
        this.dataList = this.sortCatalogue(fixedResponse);
        this.dataSource.data = this.dataList;
        this.dataSource.paginator = this.paginator;
      }
      this.customMessageService.close();
    }
    else{
      await this.customMessageService.open({title: 'Error', type: 'error', message: 'Error al cargar los datos del catálogo ' + this.catalogue, dismissBtn: true});
    }
  }

  async createEntityData(record: any){
    try{
      const createConfirm = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: `¿Seguro que quieres crear el registro <b>${record.name ?? record.code ?? ''}</b>?`,
        acceptBtn: true, dismissBtn: true,
      });

      if (createConfirm) {
        record['operationType'] = 'single'
        const service = this.callAnyEntityService(this.dataService);
        if (!service) {
          this.customMessageService.close();
          await this.customMessageService.open({
            title: 'Error', type: 'error',
            message: `No se encontró servicio para "${this.dataService}". Contacta al administrador.`,
            dismissBtn: true,
          });
          return;
        }
        const apiResponse = await service.create(record);
        this.customMessageService.close();

        if (apiResponse.state == 'success') {
          let newRecords = this.changeIds(apiResponse.data);
          this.dataList = this.sortCatalogue([...newRecords, ...this.dataList]);
          this.dataSource.data = this.dataList;
          this.dataSource.paginator = this.paginator;
          await this.customMessageService.open({
            title: 'Éxito', type: 'success', message: 'Registro creado correctamente', duration: 2000
          });
        } else {
          await this.customMessageService.open({
            title: 'Error', type: 'error',
            message: this.extractErrorMessage(apiResponse.message ?? apiResponse), duration: 3000
          });
          throw new Error(this.extractErrorMessage(apiResponse.message ?? apiResponse));
        }
      }
    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: this.extractErrorMessage(err), dismissBtn: true
      });
    }
  }

  async updateEntityData(record: any){
    try{
      const updateConfirm = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: `¿Seguro que quieres editar el registro <b>${record.name ?? record.code ?? ''}</b>?`,
        acceptBtn: true, dismissBtn: true,
      });

      if (updateConfirm) {
        record['operationType'] = 'single'
        const apiResponse = await this.callAnyEntityService(this.dataService)?.update(record);
        this.customMessageService.close();

        if (apiResponse.state == 'success') {
          let updatedRecords = this.changeIds(apiResponse.data);
          const recordIndex = this.dataList.findIndex(item => item.id === record.id);
          if (recordIndex !== -1) this.dataList[recordIndex] = updatedRecords[0];
          this.dataList = this.sortCatalogue(this.dataList);
          this.dataSource.data = this.dataList;
          this.dataSource.paginator = this.paginator;
          await this.customMessageService.open({
            title: 'Éxito', type: 'success', message: 'Registro actualizado correctamente', duration: 2000
          });
        } else {
          await this.customMessageService.open({
            title: 'Error', type: 'error',
            message: this.extractErrorMessage(apiResponse.message ?? apiResponse), duration: 3000
          });
          throw new Error(this.extractErrorMessage(apiResponse.message ?? apiResponse));
        }
      }
    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: this.extractErrorMessage(err), dismissBtn: true
      });
    }
  }

  public async deleteRecord(record: any, index: number){
    try{
      const deleteConfirm = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: `¿Seguro que quieres eliminar el registro <b>${record.name ?? record.code ?? ''}</b> de forma permanente?`,
        acceptBtn: true, dismissBtn: true,
      });

      if (deleteConfirm) {
        let data = { id: record['id'], deleted: true, operationType: 'single' }
        const apiResponse = await this.callAnyEntityService(this.dataService)?.update(data);
        this.customMessageService.close();

        if (apiResponse.state == 'success') {
          this.dataList = this.dataList.filter(item => item.id !== record.id);
          this.dataSource.data = this.dataList;
          this.dataSource.paginator = this.paginator;
          await this.customMessageService.open({
            title: 'Éxito', type: 'success', message: 'Registro eliminado correctamente', duration: 2000
          });
        } else {
          await this.customMessageService.open({
            title: 'Error', type: 'error',
            message: this.extractErrorMessage(apiResponse.message ?? apiResponse), duration: 3000
          });
          throw new Error(this.extractErrorMessage(apiResponse.message ?? apiResponse));
        }
      }
    }catch(err){
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: this.extractErrorMessage(err), dismissBtn: true
      });
    }
  }

  public async toggleActiveField(record: any, index: number): Promise<void> {
    const previousValue = record.active;
    const newValue = !previousValue;
    record.active = newValue;
    this.dataSource.data = [...this.dataList];

    try {
      const service = this.callAnyEntityService(this.dataService);
      if (!service) throw new Error(`No se encontró servicio para "${this.dataService}".`);

      const apiResponse = await service.update({
        id: record['id'], active: newValue, operationType: 'single',
      });

      if (apiResponse?.state !== 'success') {
        throw new Error(this.extractErrorMessage(apiResponse?.message ?? 'No se pudo actualizar el estado.'));
      }

      await this.customMessageService.open({
        title: 'Éxito', type: 'success',
        message: newValue ? 'Registro activado.' : 'Registro desactivado.',
        duration: 1500,
      });
    } catch (err) {
      record.active = previousValue;
      this.dataSource.data = [...this.dataList];
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: `No se pudo cambiar el estado: ${this.extractErrorMessage(err)}`,
        dismissBtn: true,
      });
    }
  }

  /* ************************************ Selección múltiple ******************** */

  /** Filas actualmente visibles en la tabla (respetando filtro). */
  private get visibleRows(): any[] {
    return (this.dataSource?.filteredData as any[]) ?? [];
  }

  /** ¿Todas las filas visibles están seleccionadas? */
  isAllSelected(): boolean {
    const visible = this.visibleRows;
    if (!visible.length) return false;
    return visible.every(row => this.selection.isSelected(row));
  }

  /** ¿Hay al menos una selección pero no todo? — para el indeterminate. */
  isPartiallySelected(): boolean {
    return this.selection.hasValue() && !this.isAllSelected();
  }

  /** Toggle del checkbox del header: marca/desmarca todas las visibles. */
  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.deselect(...this.visibleRows);
    } else {
      this.selection.select(...this.visibleRows);
    }
  }

  /** ¿Está seleccionada esta fila? — wrapper para el template. */
  isRowSelected(row: any): boolean {
    return this.selection.isSelected(row);
  }

  /** Toggle de una fila individual. */
  toggleRow(row: any): void {
    this.selection.toggle(row);
  }

  /** Cuántos seleccionados — para mostrar en el toolbar. */
  get selectedCount(): number {
    return this.selection.selected.length;
  }

  /* ************************************ Acciones masivas ******************** */

  /**
   * Cambia el campo `active` a `newValue` en todos los registros
   * seleccionados, usando el endpoint multiple del backend (un solo
   * round-trip via `updatemany`).
   */
  async bulkSetActive(newValue: boolean): Promise<void> {
    if (this.bulkActionRunning) return;
    const ids = this.selection.selected
      .map(r => r?.id)
      .filter((id: any) => !!id);
    if (!ids.length) return;
    if (!this.permissions['update']) return;

    const label = newValue ? 'activar' : 'desactivar';
    const confirm = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Seguro que quieres <b>${label}</b> los <b>${ids.length}</b> registros seleccionados?`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    this.bulkActionRunning = true;
    try {
      await this.runBulkUpdate(
        ids.map((id: string) => ({ id, active: newValue })),
        `${ids.length} registros ${newValue ? 'activados' : 'desactivados'}.`,
      );
      // Reflejar en memoria sin pegarle de nuevo al backend
      const idSet = new Set(ids);
      this.dataList.forEach(r => {
        if (idSet.has(r.id)) r.active = newValue;
      });
      this.dataSource.data = [...this.dataList];
      this.selection.clear();
    } finally {
      this.bulkActionRunning = false;
    }
  }

  /**
   * Soft-delete masivo. Equivalente a `deleteRecord` repetido N veces
   * pero en una sola llamada `updatemany` con `deleted: true`.
   */
  async bulkDelete(): Promise<void> {
    if (this.bulkActionRunning) return;
    const ids = this.selection.selected
      .map(r => r?.id)
      .filter((id: any) => !!id);
    if (!ids.length) return;
    if (!this.permissions['delete']) return;

    const confirm = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Seguro que quieres eliminar de forma permanente los <b>${ids.length}</b> registros seleccionados?`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    this.bulkActionRunning = true;
    try {
      await this.runBulkUpdate(
        ids.map((id: string) => ({ id, deleted: true })),
        `${ids.length} registros eliminados.`,
      );
      const idSet = new Set(ids);
      this.dataList = this.dataList.filter(r => !idSet.has(r.id));
      this.dataSource.data = this.dataList;
      this.selection.clear();
    } finally {
      this.bulkActionRunning = false;
    }
  }

  /**
   * Edición en lote de UN campo del catálogo.
   *
   * Flujo en 2 pasos (reutiliza CustomDialogComponent en vez de crear
   * un componente nuevo):
   *  1) Diálogo "qué campo": el usuario elige uno de los inputs del
   *     catálogo (filtramos los compuestos: contacts, services, archivos).
   *  2) Diálogo "qué valor": un único campo con el tipo y opciones del
   *     input elegido. El valor se aplica a todos los seleccionados.
   */
  async openBulkEditDialog(): Promise<void> {
    if (this.bulkActionRunning) return;
    if (!this.permissions['update']) return;

    const ids = this.selection.selected
      .map(r => r?.id)
      .filter((id: any) => !!id);
    if (!ids.length) return;

    // ── 1) Campos que se pueden bulk-editar ────────────────────────
    // Excluimos:
    //  - tipos compuestos (contacts / services) que requieren UI propia
    //  - archivos subidos (image, user_manual, etc.) que se cambian por upload
    const freshInputs = (this.setRoutes()[this.route]?.inputs ?? this.inputs) as any[];
    const editableInputs = freshInputs.filter(inp =>
      inp
      && inp.type !== 'contacts'
      && inp.type !== 'services'
      && inp.type !== 'table'
      && !this.isFileUploadColumn(inp.name)
    );

    if (!editableInputs.length) {
      await this.customMessageService.open({
        title: 'Información', type: 'neutral',
        message: 'Este catálogo no tiene campos editables en lote.',
        duration: 2200,
      });
      return;
    }

    const fieldChoiceConfig: DialogData = {
      title: `Editar ${ids.length} ${this.catPlural} — ¿qué campo?`,
      fields: [
        {
          key: '_bulkField',
          label: 'Campo a editar',
          type: 'select',
          required: true,
          options: editableInputs.map(inp => ({
            label: inp.displayName,
            value: inp.name,
          })),
        },
      ],
    };

    const fieldRef = this.dialog.open(CustomDialogComponent, {
      data: fieldChoiceConfig, width: '420px',
    });
    const fieldResult = await fieldRef.afterClosed().toPromise();
    if (!fieldResult?._bulkField) return;

    const chosenInput = editableInputs.find(inp => inp.name === fieldResult._bulkField);
    if (!chosenInput) return;

    // ── 2) Diálogo para capturar el nuevo valor ────────────────────
    // Reutilizamos el mismo mapping que `openDynamicDialog` para que
    // selects con `labelFn` muestren "INTERN_ID — Nombre" etc.
    const fieldDef: any = {
      key: chosenInput.name,
      label: chosenInput.displayName,
      type: chosenInput.type,
      required: true,
    };
    if (Array.isArray(chosenInput.options)) {
      fieldDef.options = chosenInput.options
        .filter((opt: any) => opt != null)
        .map((opt: any) => ({
          label: chosenInput.labelFn ? chosenInput.labelFn(opt) : opt.name,
          value: opt.id,
        }));
    }

    const valueRef = this.dialog.open(CustomDialogComponent, {
      data: {
        title: `Nuevo valor para "${chosenInput.displayName}"`,
        fields: [fieldDef],
      } as DialogData,
      width: '480px',
    });
    const valueResult = await valueRef.afterClosed().toPromise();
    if (!valueResult || valueResult[chosenInput.name] === undefined) return;

    const newValue = valueResult[chosenInput.name];

    // ── Confirmación final ─────────────────────────────────────────
    const confirm = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `Se actualizará <b>${chosenInput.displayName}</b> en <b>${ids.length}</b> registros. ¿Continuar?`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    this.bulkActionRunning = true;
    try {
      await this.runBulkUpdate(
        ids.map((id: string) => ({ id, [chosenInput.name]: newValue })),
        `${ids.length} registros actualizados.`,
      );
      // Refrescamos desde el backend para que columnas resueltas
      // (_supplier, _manufacturer, etc.) queden bien.
      await this.getEntityData();
    } finally {
      this.bulkActionRunning = false;
    }
  }

  /**
   * Exporta SOLO los registros seleccionados al Excel, con la misma
   * estructura que `downloadFile` (todas las columnas menos 'select'
   * y 'more', resolviendo IDs a sus labels via `_<key>`).
   */
  async exportSelectionToExcel(): Promise<void> {
    const rows = this.selection.selected;
    if (!rows.length) return;
    await this.exportRecords(rows, `${this.exportTitle()} (selección)`);
  }

  /**
   * Envoltorio común: dispara un update masivo, muestra spinner,
   * maneja errores y mensajes de éxito.
   *
   * NOTA: el backend (PatchItems en generic_builder.py) ignora silencio-
   * samente los ids que no encuentra en `current_list` con
   * `deleted: false`, así que esto es seguro incluso si el usuario
   * deja la pestaña abierta y otra persona borró registros.
   */
  private async runBulkUpdate(records: any[], successMessage: string): Promise<void> {
    if (!records.length) return;
    const service = this.callAnyEntityService(this.dataService);
    if (!service) {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: `No se encontró servicio para "${this.dataService}".`,
        dismissBtn: true,
      });
      return;
    }

    this.customMessageService.open({
      title: 'Espere', type: 'neutral',
      message: 'Aplicando cambios...', loading: true,
    });

    try {
      const apiResponse = await service.update({
        operationType: 'multiple',
        records,
      });
      this.customMessageService.close();

      if (apiResponse?.state !== 'success') {
        throw new Error(this.extractErrorMessage(
          apiResponse?.message ?? 'No se pudieron aplicar los cambios.'
        ));
      }

      await this.customMessageService.open({
        title: 'Éxito', type: 'success',
        message: successMessage, duration: 2000,
      });
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: this.extractErrorMessage(err),
        dismissBtn: true,
      });
      throw err;
    }
  }

  /**
   * Extrae un mensaje legible de cualquier formato que pueda venir del
   * backend o de un error de JS. Cubre string, Error, HttpErrorResponse,
   * objetos con .message/.detail, arrays de errores, etc.
   * Sin esto, los errores estructurados muestran "[object Object]" en
   * pantalla y no sabemos qué pasó.
   */
  private extractErrorMessage(err: any): string {
    if (err == null) return 'Error desconocido';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;

    // HttpErrorResponse de Angular
    if (err.status !== undefined && err.statusText !== undefined) {
      const body = err.error;
      if (typeof body === 'string') return `HTTP ${err.status}: ${body}`;
      if (body?.message)            return `HTTP ${err.status}: ${body.message}`;
      if (body?.detail)             return `HTTP ${err.status}: ${body.detail}`;
      return `HTTP ${err.status} ${err.statusText}`;
    }

    if (typeof err === 'object') {
      if (typeof err.message === 'string') return err.message;
      if (typeof err.detail === 'string')  return err.detail;
      if (Array.isArray(err))              return err.map(e => this.extractErrorMessage(e)).join(' • ');
      try { return JSON.stringify(err); } catch { return String(err); }
    }
    return String(err);
  }

  /**
   * Dialog para crear/editar un registro.
   *
   * Soporta `labelFn` opcional por input: si está presente, el dropdown
   * usa esa función para construir el label de cada opción. El value
   * SIEMPRE es `option.id`. Esto permite mostrar "INTERN_ID — Nombre"
   * en suppliers sin tocar el valor que se guarda en BD.
   *
   * También re-evalúa setRoutes() aquí para que las options de los
   * selects lean entityLists con datos frescos (no los vacíos del
   * primer render cuando this.inputs se cacheó en ngOnInit).
   */
  /** ¿El catálogo actual usa registros SSA por lista (equipos)? */
  usesSsaRegistry(): boolean {
    // Equipos ya no usa el modelo de activo fijo (registros SSA,
    // almacenamiento, configuraciones): solo NOMBRE | ALIAS | RESPONSABLE.
    return false;
  }

  /** Abre el diálogo para gestionar los registros SSA (reg_ssa) del equipo. */
  openSsaDialog(element: any): void {
    const ref = this.dialog.open(EquipmentSsaDialogComponent, {
      data: {
        equipmentId: element.id,
        identifier:  element.code || element.model || '',
        regSsa:      element.reg_ssa ?? [],
      },
      width: '760px', maxWidth: '95vw',
    });
    ref.afterClosed().subscribe((updated: any[] | undefined) => {
      if (!updated) return;
      element.reg_ssa = updated;
    });
  }

  /** ¿El catálogo actual usa la sección de Almacenamiento (equipos)? */
  usesStorage(): boolean {
    // Equipos ya no usa el modelo de activo fijo (registros SSA,
    // almacenamiento, configuraciones): solo NOMBRE | ALIAS | RESPONSABLE.
    return false;
  }

  /** Abre el diálogo para gestionar almacenamiento y dimensiones del equipo. */
  openStorageDialog(element: any): void {
    const ref = this.dialog.open(EquipmentStorageDialogComponent, {
      data: {
        equipmentId:        element.id,
        identifier:         element.code || element.model || '',
        storage_temp:       element.storage_temp ?? null,
        storage_humidity:   element.storage_humidity ?? null,
        storage_conditions: element.storage_conditions ?? null,
        dim_length:         element.dim_length ?? null,
        dim_width:          element.dim_width ?? null,
        dim_height:         element.dim_height ?? null,
      },
      width: '560px', maxWidth: '95vw',
    });
    ref.afterClosed().subscribe((updated: any | undefined) => {
      if (!updated) return;
      element.storage_temp       = updated.storage_temp;
      element.storage_humidity   = updated.storage_humidity;
      element.storage_conditions = updated.storage_conditions;
      element.dim_length         = updated.dim_length;
      element.dim_width          = updated.dim_width;
      element.dim_height         = updated.dim_height;
    });
  }

  /** ¿El catálogo actual usa configuraciones por equipo (equipos)? */
  usesConfigurations(): boolean {
    // Equipos ya no usa el modelo de activo fijo (registros SSA,
    // almacenamiento, configuraciones): solo NOMBRE | ALIAS | RESPONSABLE.
    return false;
  }

  /** Abre el diálogo para gestionar el arreglo `configuration` del equipo. */
  openConfigurationsDialog(element: any): void {
    const ref = this.dialog.open(EquipmentConfigurationsDialogComponent, {
      data: {
        identifier:              element.code || element.model || '',
        configuration:           element.configuration ?? [],
        accessoryIds:            Array.isArray(element.accessories) ? element.accessories : [],
        peripheralIds:           Array.isArray(element.peripheral_equipment) ? element.peripheral_equipment : [],
        gasTankIds:              Array.isArray(element.gas_tanks) ? element.gas_tanks : [],
        accessoriesList:         this.entityLists['accessoriesList'] ?? [],
        peripheralEquipmentList: this.entityLists['peripheralEquipmentList'] ?? [],
        gasTanksList:            this.entityLists['medicalGasTanksList'] ?? [],
      },
      width: '860px', maxWidth: '95vw',
    });
    ref.afterClosed().subscribe(async (updated: any[] | undefined) => {
      if (!updated) return;
      element.configuration = updated;
      try {
        const resp = await this.callAnyEntityService(this.dataService)?.update({
          id: element.id,
          configuration: updated,
          operationType: 'single',
        } as any);
        if (resp?.state !== 'success') throw new Error(resp?.message ?? 'No se pudo guardar.');
        await this.customMessageService.open({
          title: 'Éxito', type: 'success', message: 'Configuraciones guardadas.', duration: 1600,
        });
      } catch (e: any) {
        await this.customMessageService.open({
          title: 'Error', type: 'error', message: e?.message ?? 'No se pudieron guardar las configuraciones.', dismissBtn: true,
        });
      }
    });
  }

  openDynamicDialog(type: string, element?: any) {
    const freshConfig = this.setRoutes()[this.route];
    const freshInputs = freshConfig?.inputs ?? this.inputs;

    let forms = freshInputs.map((input: {
      name: string,
      displayName: string,
      type: FieldType,
      required: boolean,
      options?: any[],
      labelFn?: (opt: any) => string,
      itemKey?: string,
      detailKey?: string,
      itemLabel?: string,
      detailLabel?: string,
      columns?: any[],
      addLabel?: string,
      visibleWhen?: { field: string; equals: any | any[] },
      searchable?: boolean,
      dependsOn?: string,
      optionsMap?: { [parentValue: string]: { label: string; value: any }[] },
    }) => {
      const { name, displayName, type: inputType, required, options, labelFn,
              itemKey, detailKey, itemLabel, detailLabel,
              columns, addLabel, visibleWhen, searchable, dependsOn, optionsMap } = input;
      // Homologación de catálogos: TODO input de texto/rfid se captura y
      // muestra en MAYÚSCULAS. Esto evita registros duplicados sólo por
      // diferencia de "Marca X" vs "marca x" y unifica los reportes.
      const shouldUppercase = (inputType === 'text' || inputType === 'rfid');
      return {
        key: name,
        label: displayName,
        type: inputType,
        required: required,
        ...(shouldUppercase && { uppercase: true }),
        ...(options && {
          // Si el input declara `labelFn`, lo usamos para construir el
          // texto visible. Si no, usamos `option.name` (default histórico).
          // El value SIEMPRE es option.id — nunca enviamos el objeto entero.
          options: options
            .filter((opt: any) => opt != null)   // defensa contra commas extra que crean undefined
            .map((opt: any) => ({
              label: labelFn ? labelFn(opt) : opt.name,
              value: opt.id,
            }))
        }),
        // Configuración extra para el tipo 'services'
        ...(itemKey     && { itemKey }),
        ...(detailKey   && { detailKey }),
        ...(itemLabel   && { itemLabel }),
        ...(detailLabel && { detailLabel }),
        // Configuración extra para el tipo 'contacts' (repeater genérico)
        ...(columns  && { columns }),
        ...(addLabel && { addLabel }),
        // Visibilidad condicional (ej. CLUES sólo si tipo === 'Hospital')
        ...(visibleWhen && { visibleWhen }),
        ...(searchable !== undefined && { searchable }),
        // Select dependiente (ej. Locker según la Ubicación elegida).
        ...(dependsOn && { dependsOn }),
        ...(optionsMap && { optionsMap }),
        ...(element?.[name] !== undefined && { value: element[name] }),
        // Al crear, "Activo" arranca en Activo (true): casi todo registro nuevo
        // se da de alta para usarse. Al editar se respeta el valor guardado.
        ...(name === 'active' && type !== 'update' && element?.[name] === undefined && { value: true }),
      };
    });

    let config: DialogData = {
      title: type === 'update' ? `Editar ${this.cat}` : `${this.catIsFeminine ? 'Nueva' : 'Nuevo'} ${this.cat}`,
      fields: forms
    };

    const dialogRef = this.dialog.open(CustomDialogComponent, {
      data: config,
      width: '600px'
    });

    dialogRef.componentInstance.actionClicked.subscribe((action: string) => {});

    const ROUTES_WITH_ITEM_NUMBER = ['procedures', 'products', 'instrumentals', 'operationRooms'];

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        // ── Detección de duplicados ANTES de pegarle a la API ───────
        // Si algún campo identificador (text/rfid) ya existe en otro
        // registro, avisamos al usuario y abortamos para que pueda
        // corregirlo sin pasar por un error 4xx del backend.
        const duplicate = this.findDuplicate(
          result,
          freshInputs,
          type === 'update' ? element?.id : undefined,
          freshConfig?.unique,
        );
        if (duplicate) {
          this.customMessageService.close();
          await this.customMessageService.open({
            title: 'Registro duplicado',
            type: 'warning',
            message: `Ya existe un registro con <b>${duplicate.label}</b> = "<b>${duplicate.value}</b>". Cambia al menos uno de esos valores para continuar.`,
            dismissBtn: true,
          });
          return;
        }

        if (ROUTES_WITH_ITEM_NUMBER.includes(this.route)) {
          const contractId = result._contractId;
          const partida    = result._partida;
          delete result._contractId;
          delete result._partida;

          if (contractId && partida) {
            const existingItemNumber = type === 'update' ? (element?.itemNumber ?? {}) : {};
            result.itemNumber = { ...existingItemNumber, [contractId]: partida };
          }
        }
        if(type === 'create') await this.createEntityData(result);
        else if(type === 'update'){
          result['id'] = element['id'];
          await this.updateEntityData(result);
        }
      }
    });
  }

  /**
   * Busca si algún campo identificador del registro nuevo ya existe en
   * `this.dataList`. Solo compara campos de tipo `text` y `rfid` —los
   * mismos que homologamos a MAYÚSCULAS—, normalizando espacios y
   * acentos para que "Acme  S.A." y "acme s.a." se detecten como
   * duplicados.
   *
   * @param record    Valores que devolvió el diálogo
   * @param inputs    Definición de inputs del catálogo
   * @param excludeId Id del registro en edición (se ignora para no
   *                  marcarse a sí mismo como duplicado)
   * @param uniqueKeys Lista opcional de campos que definen la unicidad del
   *                  catálogo. Si se provee, sólo esos campos se consideran
   *                  identificadores (sin importar su tipo). Si no, se cae al
   *                  comportamiento histórico: todos los campos text/rfid.
   * @returns         El campo en conflicto, o null si no hay duplicado
   */
  private findDuplicate(
    record: any,
    inputs: any[],
    excludeId?: any,
    uniqueKeys?: string[],
  ): { key: string; label: string; value: string } | null {
    if (!Array.isArray(this.dataList) || this.dataList.length === 0) return null;

    const normalize = (v: any) =>
      (v ?? '').toString()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ').trim().toUpperCase();

    // ── Caso 1: el catálogo declara una llave única (posiblemente COMPUESTA).
    // Un registro sólo es duplicado si TODOS los campos de la llave coinciden
    // a la vez. Antes se evaluaba campo por campo (semántica OR), lo que
    // bloqueaba registros legítimos: p. ej. en Instrumentos de Medición no
    // dejaba repetir la descripción ni el modelo "N/A" aunque la marca o el
    // instrumento fueran distintos.
    if (Array.isArray(uniqueKeys) && uniqueKeys.length) {
      const keyFields = (inputs ?? []).filter((i: any) => uniqueKeys.includes(i?.name));
      if (!keyFields.length) return null;

      const existing = this.dataList.find(item => {
        if (excludeId !== undefined && item?.id === excludeId) return false;
        return keyFields.every((f: any) => normalize(item?.[f.name]) === normalize(record?.[f.name]));
      });

      if (existing) {
        const label = keyFields.map((f: any) => f.displayName ?? f.name).join(' + ');
        const value = keyFields
          .map((f: any) => (record?.[f.name] ?? '').toString().trim())
          .filter((v: string) => !!v)
          .join(' / ');
        return { key: keyFields.map((f: any) => f.name).join('+'), label, value };
      }
      return null;
    }

    // ── Caso 2 (histórico): sin `unique` declarado, cualquier campo de texto
    // o rfid repetido se considera duplicado.
    const identifierFields = (inputs ?? []).filter((i: any) => i?.type === 'text' || i?.type === 'rfid');

    for (const input of identifierFields) {
      const newValue = normalize(record?.[input.name]);
      if (!newValue) continue;

      const existing = this.dataList.find(item => {
        if (excludeId !== undefined && item?.id === excludeId) return false;
        return normalize(item?.[input.name]) === newValue;
      });

      if (existing) {
        return {
          key:   input.name,
          label: input.displayName ?? input.name,
          value: (record?.[input.name] ?? '').toString(),
        };
      }
    }
    return null;
  }

  /* ************************************ Formateo y Filtrado ***************************************************** */
  applyFilter(event: Event) {
    this.cardLimit = 60; // al buscar, las tarjetas vuelven a empezar
    const filterValue = (event.target as HTMLInputElement).value
      .replace(/&|<|>|"|'/g, match => ({'&': '&amp;','<': '&lt;','>': '&gt;','"': '&quot;',"'": '&#39;'}[match]!))
      .replace(/[^a-zA-Z0-9áéíóú-\s]/g, '')
      .trim();
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.paginator.length = this.dataSource.filteredData.length;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Orden de TODOS los catálogos: alfabético por el alias (si el catálogo
   * tiene alias propio —Sitios— o se liga a un sitio por alias —Equipos,
   * Almacenes, Instrumental—) y después por el nombre (llave `name`). Los
   * catálogos sin `name` se ordenan por su primera columna.
   */
  private sortCatalogue(list: any[]): any[] {
    const text = (v: any) => (v === null || v === undefined ? '' : String(v)).trim();
    const firstKey = (this.keys ?? []).find(k => k !== 'select' && k !== 'more');
    const aliasOf = (r: any) => text(r?.alias ?? r?._place);
    const nameOf = (r: any) => text(r?.name ?? (firstKey ? (r?.['_' + firstKey] ?? r?.[firstKey]) : ''));
    const cmp = (a: string, b: string) => {
      if (!a && !b) return 0;
      if (!a) return 1;     // vacíos al final
      if (!b) return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base', numeric: true });
    };
    return [...(list ?? [])].sort((a, b) => cmp(aliasOf(a), aliasOf(b)) || cmp(nameOf(a), nameOf(b)));
  }

  private applyAlphabeticSorting(data: any[], key: string) {

    // Por nombre: alias primero (si el catálogo lo tiene), luego nombre/código.

    if (key === 'name') return sortCatalog(data ?? []);
    const sortedData = [...data];

    const normalize = (str: string) =>
      str?.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ").trim().toLowerCase() ?? "";

    sortedData.sort((a, b) => {
      const valueA = normalize(a?.[key] as string);
      const valueB = normalize(b?.[key] as string);

      if (!valueA && !valueB) return 0;
      if (!valueA) return 1;
      if (!valueB) return -1;

      const numA = parseInt(valueA, 10);
      const numB = parseInt(valueB, 10);

      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB;
      if (!isNaN(numA) && isNaN(numB)) return -1;
      if (isNaN(numA) && !isNaN(numB)) return 1;

      const textA = valueA.replace(/^\d+\s*/, "");
      const textB = valueB.replace(/^\d+\s*/, "");

      return textA.localeCompare(textB, "es", { sensitivity: "base", ignorePunctuation: true });
    });

    return sortedData;
  }

  /**
   * Resuelve una celda de Excel con uno o varios nombres (separados por
   * coma, punto y coma, barra o salto de línea) a un ARREGLO de ids del
   * catálogo indicado. Se usa para campos multi-selección como
   * `specialty` en Equipos. Los nombres que no se encuentran se omiten.
   */
  private mapNamesToIds(value: any, catalog: any[], nameField = 'name'): any[] {
    if (value === null || value === undefined || value.toString().trim() === '') return [];
    const norm = (s: any) => (s ?? '').toString()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ').trim().toLowerCase();
    const names = value.toString().split(/[,;|\n]+/).map((n: string) => norm(n)).filter((n: string) => !!n);
    const ids: any[] = [];
    for (const name of names) {
      const found = (catalog ?? []).find((c: any) => norm(c?.[nameField]) === name);
      if (found && !ids.includes(found.id)) ids.push(found.id);
    }
    return ids;
  }

  private transformToBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (!value) return false;
    const normalized = value.toString().toLowerCase().trim();
    const trueValues = ['activo', 'true', 'sí', 'si', '1', 'yes'];
    return trueValues.includes(normalized);
  }

  // ─── Helpers de normalización para carga por Excel ─────────────────────

  private isExcelEmpty(value: any): boolean {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  }

  private normalizeText(value: any): string | null {
    if (this.isExcelEmpty(value)) return null;
    return value.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ').trim().toUpperCase();
  }

  /** Mayúsculas conservando acentos y Ñ (nombres de médicos, líneas
   *  'TÓRAX'…). Colapsa espacios, incluidos los espacios duros de Excel. */
  /** Alias del sitio (o su nombre si no tiene alias). */
  private placeAlias(id: any): string {
    if (!id) return '';
    const place = (this.entityLists?.['placesList'] ?? []).find((p: any) => p?.id === id);
    return place ? (place.alias || place.name || '') : '';
  }

  private upperText(value: any): string | null {
    if (this.isExcelEmpty(value)) return null;
    const text = value.toString().replace(/\s+/g, ' ').trim().toUpperCase();
    return text || null;
  }

  /** Texto tal cual (notas), solo sin espacios sobrantes. */
  private plainText(value: any): string | null {
    if (this.isExcelEmpty(value)) return null;
    const text = value.toString().replace(/\s+/g, ' ').trim();
    return text || null;
  }

  /** Columna Activo opcional: si no viene o está vacía, el registro entra activo. */
  private activeOrTrue(value: any): boolean {
    if (this.isExcelEmpty(value)) return true;
    return this.transformToBoolean(value);
  }

  /** TIPO del archivo de Sitios → valor del catálogo. */
  private placeTypeFromExcel(value: any): string | null {
    const text = this.normalizeText(value);
    if (!text) return null;
    const map: Record<string, string> = {
      'HOSPITAL': 'HOSPITAL',
      'MACROCENTRO': 'MACROCENTRO',
      'ALMACEN LOCAL': 'ALMACEN LOCAL',
      'CEDIS': 'CEDIS',
      'ALMACEN': 'ALMACEN',
    };
    return (map[text] ?? text).toUpperCase();
  }

  /** Hoja de correcciones del archivo de Médicos (NOMBRE INCORRECTO →
   *  NOMBRE CORRECTO), como mapa normalizado incorrecto → correcto. */
  private doctorNameFixes(sheets: Record<string, any[]>): Map<string, string> {
    const fixes = new Map<string, string>();
    for (const rows of Object.values(sheets ?? {})) {
      for (const row of rows ?? []) {
        const keys = Object.keys(row ?? {});
        const wrongKey = keys.find(k => this.normalizeText(k) === 'NOMBRE INCORRECTO');
        const rightKey = keys.find(k => this.normalizeText(k) === 'NOMBRE CORRECTO');
        if (!wrongKey || !rightKey) continue;
        const wrong = this.upperText(row[wrongKey]);
        const right = this.upperText(row[rightKey]);
        if (wrong && right && wrong !== right) fixes.set(wrong, right);
      }
    }
    return fixes;
  }

  /** Antes de agrupar: cambia los nombres mal escritos por el correcto,
   *  para que no se den de alta dos veces el mismo médico. */
  private applyDoctorNameFixes(rows: any[], sheets: Record<string, any[]>): any[] {
    const fixes = this.doctorNameFixes(sheets);
    if (fixes.size === 0) return rows;
    return rows.map(row => {
      const nameKey = Object.keys(row ?? {}).find(k => this.normalizeText(k) === 'NOMBRE');
      if (!nameKey) return row;
      const fixed = fixes.get(this.upperText(row[nameKey]) ?? '');
      return fixed ? { ...row, [nameKey]: fixed } : row;
    });
  }

  /** Después de agrupar: guarda en cada médico sus nombres mal escritos. */
  private attachDoctorNameVariants(items: any[], sheets: Record<string, any[]>): any[] {
    const fixes = this.doctorNameFixes(sheets);
    const byName = new Map<string, string[]>();
    fixes.forEach((right, wrong) => byName.set(right, [...(byName.get(right) ?? []), wrong]));
    return items.map(item => ({ ...item, nameVariants: byName.get(item?.name) ?? [] }));
  }

  private normalizeEmail(value: any): string | null {
    if (this.isExcelEmpty(value)) return null;
    return value.toString().trim().toLowerCase();
  }

  private normalizeNumber(value: any): number | null {
    if (this.isExcelEmpty(value)) return null;
    if (typeof value === 'number') return value;
    const cleaned = value.toString().replace(/,/g, '.').trim();
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizeDate(value: any): string | null {
    if (this.isExcelEmpty(value)) return null;
    if (value instanceof Date) return value.toISOString().split('T')[0];

    const str = value.toString().trim();
    const dmyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmyMatch) {
      const [, d, m, y] = dmyMatch;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return null;
  }

  private transformToArray(value: any): string[] {
    if (!value) return [];
    const valueStr = value.toString();
    return valueStr.split(/\r?\n/).map((item: string) => item.trim())
      .filter((item: string | any[]) => item.length > 0);
  }

  private buildItemNumber(items: any[], existingList: any[], targetColumn: string): any[] {
    return items.map(item => {
      const contractId = item._contractId;
      const partida    = item._partida;
      delete item._contractId;
      delete item._partida;

      if (!contractId || !partida) return item;

      const existingRecord = existingList?.find((r: any) => r[targetColumn] === item[targetColumn]);
      item.itemNumber = { ...(existingRecord?.itemNumber ?? {}), [contractId]: partida };
      return item;
    });
  }

  formatItemNumber(itemNumber: any): string[] {
    if (!itemNumber || typeof itemNumber !== 'object') return [];
    const entries = Object.entries(itemNumber);
    if (entries.length === 0) return [];

    return entries.map(([contractId, partida]) => {
      const contract = this.entityLists['contractList'] ?? []?.find((c: any) => c.id === contractId);
      const contractName = contract?.name ?? contractId;
      return `${contractName} - ${partida}`;
    });
  }

  /* ************************************ Archivos ***************************************************** */
  async openUploadFile() {
    try {
      let config: ExcelUploaderConfig | any = this.setExcelRoutes()[this.route] ?? {};
      if (config) {
        const resolvedService = this.callAnyEntityService(config.entityName);
        if (!resolvedService) {
          await this.customMessageService.open({
            title: 'Error', type: 'error',
            message: `No se encontró servicio para "${config.entityName}". Contacta al administrador.`,
            dismissBtn: true,
          });
          return;
        }

        config.services = {
          uploadFileDialog: this.uploadFileDialog,
          customMessageService: this.customMessageService,
          dataService: resolvedService,
        };
        config.search = { deleted: false }
        const result = await this.simpleExcelUpload.uploadExcelFile(config);

        if (result) {
          let finalData: any[] = [];

          if (config.skipSave) {
            const targetColumn = config.validation.targetColumn;
            const itemsToCreate = this.buildItemNumber(result.uniqueItems, this.dataList, targetColumn);
            const itemsToUpdate = this.buildItemNumber(result.itemsToUpdate, this.dataList, targetColumn);

            if (itemsToCreate.length > 0) {
              await resolvedService.create({ operationType: 'multiple', records: itemsToCreate });
            }
            if (itemsToUpdate.length > 0) {
              await resolvedService.update({ operationType: 'multiple', records: itemsToUpdate });
            }

            const searchResponse = await resolvedService.search({
              condition: { ...config.search }, searchType: 'default'
            });
            finalData = searchResponse.data;
          } else {
            finalData = result;
          }
          let fixedResponse = this.changeIds(finalData);
          this.dataList = this.sortCatalogue(fixedResponse);
          this.dataSource.data = this.dataList;
          this.dataSource.paginator = this.paginator;
        }
      }
    } catch (err) {
      console.error('[openUploadFile] Error:', err);
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: `Error al procesar el archivo: ${this.extractErrorMessage(err)}`,
        dismissBtn: true
      });
    }
  }

  /**
   * Descarga un archivo Excel vacío usado como plantilla de carga.
   *
   * IMPORTANTE: las columnas DEBEN ser idénticas a las que `setExcelRoutes`
   * acepta al hacer "Cargar archivo", si no el upload encuentra columnas
   * con nombres distintos y silenciosamente deja campos vacíos.
   * Por eso esta función lee el mismo `setExcelRoutes()` que usa la carga
   * en vez de armarse a partir de `this.inputs` (los inputs del dialog
   * de edición pueden incluir campos calculados que no se piden en
   * Excel — ej. `services` en places — o tener nombres distintos como
   * "Razón Social" vs "Razon social").
   */
  async downloadSampleFile() {
    const excelConfig = this.setExcelRoutes()[this.route];

    let tableStruct: TableStructureModel[] = [];
    if (excelConfig?.columns?.length) {
      excelConfig.columns.forEach((col: any) => {
        tableStruct.push({columnKey: col.objectKey, columnName: col.excelColumn, type: 'string'});
      });
    } else {
      // Fallback: si no hay config de Excel para este catálogo, caemos
      // a los inputs del formulario para que la descarga siga funcionando.
      this.inputs.forEach(input => {
        tableStruct.push({columnKey: input.name, columnName: input.displayName, type: 'string'});
      });
    }

    await this.simpleExcel.simpleXLSX({
      fileName: `${this.exportTitle()} - Archivo muestra`,
      sheets: [{
        sheetName: 'Elementos',
        columns: tableStruct,
        data: [],
        header: [],
        lineBreak: 0,
      }],
    });
  }

  /** Título del Excel y nombre del archivo: la etiqueta del catálogo tal cual
   *  se ve en la app (p. ej. "Cirugía", "Especialidad"). */
  /** Género/plural del nombre del registro (ubicación → Nueva / ubicaciones). */
  get catIsFeminine(): boolean {
    const w = String(this.cat ?? '').trim().toLowerCase().split(' ')[0];
    return /(a|ión|dad|tad|umbre)$/.test(w) && !/^(sistema|tema|problema|día)$/.test(w);
  }
  get catPlural(): string {
    const words = String(this.cat ?? '').trim().split(' ');
    const w = words[0] ?? '';
    const special: Record<string, string> = { locker: 'lockers', kit: 'kits' };
    const pl = special[w.toLowerCase()] ? special[w.toLowerCase()]
      : /ón$/.test(w) ? w.replace(/ón$/, 'ones')
      : /[aeiouáéó]$/i.test(w) ? w + 's'
      : /z$/.test(w) ? w.replace(/z$/, 'ces') : w + 'es';
    return [pl, ...words.slice(1).map(x => /[aeiou]$/i.test(x) ? x + 's' : x)].join(' ');
  }

  /** Título de la pantalla: "Catálogo de <etiqueta del módulo>". */
  get pageTitle(): string {
    const label = (this.catalogue || '').trim();
    if (!label) return '';
    return /^cat[aá]logo/i.test(label) ? label : `Catálogo de ${label}`;
  }

  private exportTitle(): string {
    return this.catalogue || this.routeToHumanLabel(this.route);
  }

  /** Nombre de hoja válido para Excel (máx. 31 caracteres, sin : \\ / ? * [ ]). */
  private sheetNameOf(text: string): string {
    return text.replace(/[:\\/?*\[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31);
  }

  /** Valor legible de una celda para exportar (ids → nombre, booleanos → texto). */
  private exportValue(row: any, key: string, catalogMapping?: any): any {
    let value = row?.[key];
    if (catalogMapping) {
      const ids = Array.isArray(value) ? value : (value === null || value === undefined || value === '' ? [] : [value]);
      return ids.map((id: any) => (catalogMapping.catalog ?? []).find((c: any) => c?.id === id)?.[catalogMapping.nameField] ?? '')
        .filter((t: any) => t !== '').join(', ');
    }
    // Columnas resueltas por changeIds (`_<key>`): ya traen el texto legible.
    if (row && row['_' + key] !== undefined && row['_' + key] !== null && (Array.isArray(value) || typeof value === 'string' || value == null)) {
      const resolved = row['_' + key];
      if (resolved !== '' || value == null) return resolved;
    }
    if (typeof value === 'boolean') return key === 'active' ? (value ? 'ACTIVO' : 'INACTIVO') : (value ? 'SÍ' : 'NO');
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.map(v => (v !== null && typeof v === 'object') ? (v.name ?? v.code ?? '') : v).filter(v => v !== '' && v != null).join(', ');
    if (typeof value === 'object') return value.name ?? value.code ?? '';
    return value;
  }

  /**
   * Arma columnas y renglones del Excel de un catálogo.
   *
   * Si el catálogo tiene configuración de carga (`setExcelRoutes`), la
   * descarga usa EXACTAMENTE esas columnas (los mismos encabezados que el
   * archivo muestra y la carga), para que lo descargado se pueda volver a
   * cargar. Los catálogos agrupados salen como en el archivo original: una
   * fila por producto (Servicios) o por hospital (Médicos).
   * Sin configuración de carga, usa las columnas de la tabla (sin selección,
   * acciones ni archivos).
   */
  private buildExport(records: any[]): { columns: TableStructureModel[]; data: any[] } {
    const config: any = this.setExcelRoutes()[this.route];
    const cfgColumns: any[] = config?.columns ?? [];

    if (!cfgColumns.length) {
      const keys = this.keys.filter(k => k !== 'select' && k !== 'more' && !(k in FILE_UPLOAD_COLUMNS));
      const columns = keys.map((key, i) => ({ columnKey: 'c' + i, columnName: this.keyNames[this.keys.indexOf(key)] ?? key, type: 'string' } as TableStructureModel));
      const data = records.map(r => Object.fromEntries(keys.map((key, i) => ['c' + i, this.exportValue(r, key)])));
      return { columns, data };
    }

    const gc = config.groupedConfig;
    const itemCols: any[] = gc?.arrayItemColumns ?? [];
    const isItemCol = (c: any) => itemCols.some(ic => ic.excelColumn === c.excelColumn) || (gc?.arrayColumn && c.excelColumn === gc.arrayColumn);
    const columns = cfgColumns.map((c, i) => ({ columnKey: 'c' + i, columnName: c.excelColumn, type: 'string' } as TableStructureModel));

    const baseRow = (r: any) => Object.fromEntries(cfgColumns.map((c, i) =>
      [('c' + i), isItemCol(c) || String(c.objectKey).startsWith('_') ? '' : this.exportValue(r, c.objectKey, c.catalogMapping)]));

    const data: any[] = [];
    for (const r of records) {
      const base = baseRow(r);
      if (gc && itemCols.length) {
        const items: any[] = Array.isArray(r?.[gc.arrayObjectKey]) ? r[gc.arrayObjectKey] : [];
        if (!items.length) { data.push(base); continue; }
        for (const item of items) {
          const row = { ...base };
          cfgColumns.forEach((c, i) => {
            const ic = itemCols.find(x => x.excelColumn === c.excelColumn);
            if (ic) row['c' + i] = this.exportValue(item, ic.objectKey, ic.catalogMapping);
          });
          data.push(row);
        }
      } else if (gc?.arrayColumn) {
        const ids: any[] = Array.isArray(r?.[gc.arrayObjectKey]) ? r[gc.arrayObjectKey] : [];
        const idx = cfgColumns.findIndex(c => c.excelColumn === gc.arrayColumn);
        if (!ids.length) { data.push(base); continue; }
        for (const id of ids) {
          data.push({ ...base, ['c' + idx]: this.exportValue({ v: id }, 'v', gc.arrayCatalogMapping) });
        }
      } else {
        data.push(base);
      }
    }
    return { columns, data };
  }

  /** Hojas extra que la carga también lee (Médicos: correcciones de nombre). */
  private extraExportSheets(records: any[]): any[] {
    if (this.route !== 'doctors') return [];
    const data = records.flatMap(r => (Array.isArray(r?.nameVariants) ? r.nameVariants : [])
      .map((wrong: string) => ({ wrong, right: r.name })));
    if (!data.length) return [];
    return [{
      sheetName: 'ML_BASE_ERRORES', header: [], lineBreak: 0, data,
      columns: [
        { columnKey: 'wrong', columnName: 'NOMBRE INCORRECTO', type: 'string' },
        { columnKey: 'right', columnName: 'NOMBRE CORRECTO', type: 'string' },
      ],
    }];
  }

  private async exportRecords(records: any[], fileName: string): Promise<void> {
    const { columns, data } = this.buildExport(records ?? []);
    const title = this.exportTitle();
    await this.simpleExcel.simpleXLSX({
      fileName,
      sheets: [{
        // La hoja lleva solo el nombre del catálogo (Excel corta a 31 caracteres).
        sheetName: this.sheetNameOf(this.catalogue || title),
        columns,
        data,
        header: [title],
        lineBreak: 1,
      }, ...this.extraExportSheets(records ?? [])],
    });
  }

  async downloadFile(){
    await this.exportRecords(this.dataList ?? [], this.exportTitle());
  }

  backMenu() {
    this.location.back();
  }
}